import React, {
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
  useCallback,
  useEffect,
  useMemo,
} from "react";
import useImage from "use-image";
import {
  AlertCircle,
  Check,
  Copy,
  DoorOpen,
  Eraser,
  Eye,
  Grid3X3,
  History,
  LoaderCircle,
  MousePointer2,
  PencilRuler,
  Redo2,
  RotateCcw,
  Square,
  SquareDashed,
  Trash2,
  Undo2,
  X,
  ZoomIn,
  ZoomOut,
} from "lucide-react";
import HatchSwatch from "./HatchSwatch";
import { renderMaskLayer, canvasToBase64 } from "../utils/maskRender";
import API_BASE_URL from "../config";
import "./TakeoffEditorStudio.css";

const ZOOM_MIN = 0.5;
const ZOOM_MAX = 4;
const ZOOM_STEP = 0.25;
// Safety margin (px) subtracted from the fitted stage size so rounding
// never produces scrollbars at 100% zoom.
const STAGE_FIT_MARGIN = 2;

const MIN_PAINT_DIM =
  Number(
    (window._env_ && window._env_.REACT_APP_TAKEOFF_MIN_PAINT_DIM) ||
      process.env.REACT_APP_TAKEOFF_MIN_PAINT_DIM,
  ) || 1024;

// Width of the reference canvas all design constants below are expressed in
// (the Blueprint Studio canvas.jsx prototype). Sizes are rescaled to the
// actual plan resolution so the editor looks identical on any image.
const DESIGN_REF_DIM =
  Number(
    (window._env_ && window._env_.REACT_APP_TAKEOFF_EDITOR_DESIGN_REF) ||
      process.env.REACT_APP_TAKEOFF_EDITOR_DESIGN_REF,
  ) || 1600;

const HISTORY_LIMIT =
  Number(
    (window._env_ && window._env_.REACT_APP_TAKEOFF_HISTORY_LIMIT) ||
      process.env.REACT_APP_TAKEOFF_HISTORY_LIMIT,
  ) || 30;

// Debounce (ms) before an edit (wall add/move/delete, erase stroke, thickness
// change) triggers an automatic no-VLM preview estimate — near-live feedback
// without re-running the slow full recompute on every keystroke.
const PREVIEW_DEBOUNCE_MS =
  Number(
    (window._env_ && window._env_.REACT_APP_TAKEOFF_PREVIEW_DEBOUNCE_MS) ||
      process.env.REACT_APP_TAKEOFF_PREVIEW_DEBOUNCE_MS,
  ) || 800;

// Minimum on-canvas size (DESIGN_REF_DIM-space, scaled via sz()) of a detected
// wall's invisible hover/click hit box. Real masonry walls often render only a
// couple of CSS px thick at plan scale — far below what a real mouse can aim
// at — so the hit box is padded independently of the wall's visual thickness;
// the visible highlight outline pads along with it, which reads as a normal
// selection halo rather than a bug.
const MIN_WALL_HIT_PX =
  Number(
    (window._env_ && window._env_.REACT_APP_TAKEOFF_MIN_WALL_HIT_PX) ||
      process.env.REACT_APP_TAKEOFF_MIN_WALL_HIT_PX,
  ) || 14;

// Uniform outward padding (all 4 sides, DESIGN_REF_DIM-space, scaled via sz())
// added to every detected wall's hit box, independent of MIN_WALL_HIT_PX.
// Swallows the slack between the fitted rectangle and the actually-rendered
// hatch pixels so a wall stays comfortably hoverable along its full edge, not
// just past a razor-thin boundary. Junction/corner gaps are handled
// separately and precisely by the backend's `hit_bbox_pct` (see
// group_walls_by_material_adjacency in wall_network.py) - this padding is
// only for on-screen click comfort.
const WALL_HIT_EDGE_PAD_PX =
  Number(
    (window._env_ && window._env_.REACT_APP_TAKEOFF_WALL_HIT_EDGE_PAD_PX) ||
      process.env.REACT_APP_TAKEOFF_WALL_HIT_EDGE_PAD_PX,
  ) || 4;

// Fallback wall-thickness presets (real-world mm) used when the backend has no
// detected thicknesses for this floor yet (e.g. an empty mask, or a legacy run
// predating this field).
const DEFAULT_THICKNESS_PRESETS_MM = (
  (window._env_ && window._env_.REACT_APP_TAKEOFF_THICKNESS_PRESETS_MM) ||
  process.env.REACT_APP_TAKEOFF_THICKNESS_PRESETS_MM ||
  "115,175,240,300,365,425,500"
)
  .split(",")
  .map((value) => Number(value.trim()))
  .filter((value) => Number.isFinite(value) && value > 0);

// All values are in DESIGN_REF_DIM-space and multiplied by the per-image
// scale factor at runtime.
const DESIGN = {
  eraserDefault: 34,
  eraserMin: 8,
  eraserMax: 160,
  gridDefault: 40,
  gridMin: 10,
  gridMax: 160,
  wallThickness: 14,
  wallThicknessMin: 8,
  wallThicknessMax: 36,
  openingWidth: 95,
  openingWidthMin: 24,
  openingWidthMax: 400,
  roomCloseDistance: 28,
  handleRadius: 11,
  handleStroke: 5,
  wallMergeGap: 18,
  wallMergeLineDistance: 4,
  duplicateOffset: 28,
  selectionExtra: 10,
  roomLabelFont: 24,
  roomLabelOffsetX: 18,
  roomLabelOffsetY: 34,
  gridMinorStroke: 1,
  gridMajorStroke: 2,
  draftStroke: 5,
  draftPointRadius: 8,
  draftStartRadius: 10,
  openingLeafHalf: 12,
};

// Display color for the AI-detected wall mask — matches the outer-wall red
// used for drawn vector walls (canvas.jsx palette). Display-only; the
// exported mask stays white-on-black.
const DETECTED_WALL_COLOR =
  (window._env_ && window._env_.REACT_APP_TAKEOFF_MASK_COLOR) ||
  process.env.REACT_APP_TAKEOFF_MASK_COLOR ||
  "#bf2f37";

const ROOM_COLORS = [
  "#f6c863",
  "#8ee2b0",
  "#a9c9f8",
  "#d5c4f7",
  "#ffc0dc",
  "#f8b28f",
  "#94e4d1",
];

// Drawing-canvas palette. These colours ENCODE DATA on the plan (grid, paper,
// selection outlines, review flags, openings, draft geometry) and must stay
// distinct from the app's brand/status tokens - they are deliberately NOT
// mapped onto --ds-* variables. SVG presentation attributes cannot read CSS
// custom properties, so they live here instead of TakeoffEditorStudio.css.
const CANVAS_COLORS = {
  paper: "#f8fafb",
  gridMinor: "#dce3e8",
  gridMajor: "#c7d0d8",
  selectionOutline: "#101827", // selected detected wall group / drawn wall halo
  selectedShape: "#111827", // selected room / opening outline
  needsReview: "#92600a", // dashed outline on walls flagged for review
  opening: "#7a8792",
  draft: "#0f9b8e", // in-progress wall/room points (matches .tes-handle)
};

// Overall-confidence level (high / medium / low, any case) -> design-system
// badge class. Shared by the editor's Data quality panel and the takeoff
// history list so the same confidence always reads in the same tone.
const CONFIDENCE_TONES = { high: "success", medium: "warning", low: "danger" };
export function confidenceBadgeClass(level) {
  const tone = CONFIDENCE_TONES[String(level || "").toLowerCase()];
  return tone ? `ds-badge ds-badge--${tone}` : "ds-badge";
}

const TOOLS = ["select", "wall", "room", "opening", "erase"];

// ── Geometry helpers ───────────────────────────────────
function clone(value) {
  return structuredClone(value);
}

function uid(prefix) {
  return `${prefix}-${Math.random().toString(36).slice(2, 9)}`;
}

function distance(a, b) {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

// Shortest distance from (px,py) to the segment (x1,y1)-(x2,y2). Standard
// clamped-projection formula.
function pointToSegmentDistance(px, py, x1, y1, x2, y2) {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const lenSq = dx * dx + dy * dy;
  let t = lenSq > 0 ? ((px - x1) * dx + (py - y1) * dy) / lenSq : 0;
  t = Math.max(0, Math.min(1, t));
  const nx = x1 + t * dx;
  const ny = y1 + t * dy;
  return Math.hypot(px - nx, py - ny);
}

function wallLength(wall) {
  return distance(wall.points[0], wall.points[1]);
}

function setWallLengthFromStart(wall, length) {
  const [start, end] = wall.points;
  const current = Math.max(1, distance(start, end));
  const unit = {
    x: (end.x - start.x) / current,
    y: (end.y - start.y) / current,
  };
  wall.points[1] = {
    x: start.x + unit.x * length,
    y: start.y + unit.y * length,
  };
}

function lineDirection(wall) {
  const [a, b] = wall.points;
  const len = Math.max(1, distance(a, b));
  return { x: (b.x - a.x) / len, y: (b.y - a.y) / len };
}

// Two walls count as "the same line" when they are (anti)parallel, lie on the
// same infinite line within tolerance, and overlap or nearly touch.
function isSameWallLine(existing, candidate, tolerance) {
  const a = existing.points[0];
  const b = existing.points[1];
  const c = candidate.points[0];
  const d = candidate.points[1];
  const dirA = lineDirection(existing);
  const dirB = lineDirection(candidate);
  const sameAngle =
    Math.abs(Math.abs(dirA.x * dirB.x + dirA.y * dirB.y) - 1) < 0.03;
  const lineDistance =
    Math.abs((b.x - a.x) * (a.y - c.y) - (a.x - c.x) * (b.y - a.y)) /
    Math.max(1, distance(a, b));
  const endDistance =
    Math.abs((b.x - a.x) * (a.y - d.y) - (a.x - d.x) * (b.y - a.y)) /
    Math.max(1, distance(a, b));
  const existingT = existing.points
    .map((point) => (point.x - a.x) * dirA.x + (point.y - a.y) * dirA.y)
    .sort((left, right) => left - right);
  const candidateT = candidate.points
    .map((point) => (point.x - a.x) * dirA.x + (point.y - a.y) * dirA.y)
    .sort((left, right) => left - right);
  const gap =
    Math.max(existingT[0], candidateT[0]) -
    Math.min(existingT[1], candidateT[1]);
  const overlapsOrTouches = gap < tolerance.gap;
  return (
    sameAngle &&
    lineDistance < tolerance.line &&
    endDistance < tolerance.line &&
    overlapsOrTouches
  );
}

function mergeWallOntoLine(existing, candidate) {
  const origin = existing.points[0];
  const dir = lineDirection(existing);
  const allPoints = [...existing.points, ...candidate.points];
  const projected = allPoints.map((point) => ({
    point,
    t: (point.x - origin.x) * dir.x + (point.y - origin.y) * dir.y,
  }));
  const min = projected.reduce(
    (best, item) => (item.t < best.t ? item : best),
    projected[0],
  );
  const max = projected.reduce(
    (best, item) => (item.t > best.t ? item : best),
    projected[0],
  );
  existing.points = [
    { x: origin.x + dir.x * min.t, y: origin.y + dir.y * min.t },
    { x: origin.x + dir.x * max.t, y: origin.y + dir.y * max.t },
  ];
  existing.thickness = candidate.thickness;
  existing.thicknessMm = candidate.thicknessMm;
  existing.materialKey = candidate.materialKey;
  existing.wallType = candidate.wallType;
}

// Cheap reject before the trig-heavy isSameWallLine check below: a same-line
// candidate's endpoints can be at most tolerance.gap apart along the line and
// tolerance.line apart perpendicular to it, so padding a plain bbox overlap
// test by their sum in both axes can only ever accept too much, never wrongly
// reject a pair the full check would have merged.
function wallBBoxMayOverlap(existing, candidate, tolerance) {
  const padding = tolerance.gap + tolerance.line;
  const [a1, a2] = existing.points;
  const [b1, b2] = candidate.points;
  return (
    Math.min(a1.x, a2.x) - padding <= Math.max(b1.x, b2.x) &&
    Math.max(a1.x, a2.x) + padding >= Math.min(b1.x, b2.x) &&
    Math.min(a1.y, a2.y) - padding <= Math.max(b1.y, b2.y) &&
    Math.max(a1.y, a2.y) + padding >= Math.min(b1.y, b2.y)
  );
}

function upsertWall(elements, wall, tolerance) {
  const sameLineWall = elements.find(
    (item) =>
      item.type === "wall" &&
      wallBBoxMayOverlap(item, wall, tolerance) &&
      isSameWallLine(item, wall, tolerance),
  );
  if (sameLineWall) {
    mergeWallOntoLine(sameLineWall, wall);
    return sameLineWall.id;
  }
  elements.push(wall);
  return wall.id;
}

// Canvas/SVG round-cap strokes of zero length render nothing; nudge single
// points so a click still produces a visible dot.
function ensureRenderableStroke(flatPoints) {
  if (flatPoints.length === 2) {
    return [...flatPoints, flatPoints[0] + 0.01, flatPoints[1]];
  }
  return flatPoints;
}

function flatToPointsAttr(flatPoints) {
  const pairs = [];
  for (let i = 0; i + 1 < flatPoints.length; i += 2) {
    pairs.push(`${flatPoints[i]},${flatPoints[i + 1]}`);
  }
  return pairs.join(" ");
}

function ToolbarButton({
  active,
  title,
  label,
  className,
  onClick,
  disabled,
  children,
}) {
  return (
    <button
      type="button"
      className={`tes-toolButton ${className || ""} ${active ? "active" : ""}`}
      aria-pressed={typeof active === "boolean" ? active : undefined}
      title={title}
      onClick={onClick}
      disabled={disabled}
    >
      {children}
      {label && <span>{label}</span>}
    </button>
  );
}

// Loads one HTMLImageElement per material-mask layer. Unlike use-image (one
// hook call per image), the layer count is dynamic per document/floor, so
// this batches all layer loads into a single effect keyed by a stable string
// (url list) rather than the array reference, which changes every render.
function useLayerImages(layers) {
  const layersRef = useRef(layers);
  layersRef.current = layers;
  const layersKey = layers.map((layer) => `${layer.key}:${layer.url}`).join("|");
  const [state, setState] = useState({ images: {}, status: "loading" });

  useEffect(() => {
    const current = layersRef.current;
    if (!current.length) {
      setState({ images: {}, status: "loaded" });
      return undefined;
    }
    let cancelled = false;
    const nextImages = {};
    let remaining = current.length;
    const finish = () => {
      if (cancelled || remaining > 0) return;
      setState({
        images: nextImages,
        status: Object.keys(nextImages).length ? "loaded" : "failed",
      });
    };
    current.forEach((layer) => {
      if (!layer.url) {
        remaining -= 1;
        finish();
        return;
      }
      const img = new window.Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        nextImages[layer.key] = img;
        remaining -= 1;
        finish();
      };
      img.onerror = () => {
        remaining -= 1;
        finish();
      };
      img.src = layer.url;
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layersKey]);

  return state;
}

// One validation flag/escalation reason (validation.py's structured
// {message, source_page, wall_id, bbox_pct} — see wall_needs_review's
// docstring for why this is backend data, never re-derived here). When
// source_page is present, a "Go to page N" action lets the user jump
// straight to the offending floor instead of hunting for it manually.
function ReasonRow({ reason, currentPageIndex, onJumpToReason }) {
  const sourcePage = reason?.source_page;
  const canJump = sourcePage != null && Boolean(onJumpToReason);
  return (
    <li className="tes-reasonRow">
      <span>{reason?.message}</span>
      {canJump && (
        <button
          type="button"
          className="tes-reasonJumpBtn"
          onClick={() => onJumpToReason(reason)}
        >
          {sourcePage === currentPageIndex ? "Select wall →" : `Go to page ${sourcePage} →`}
        </button>
      )}
    </li>
  );
}

// Human-readable one-liner for one wall_review_history row (see
// change_history.py's diff_and_record_wall_changes — before_value/after_value
// are plain scalars for material/wall_type/thickness, or a compact
// {material, wall_type, thickness_mm, full_length_m} snapshot for an
// added/removed wall).
function formatHistoryChange(entry) {
  const { change_type: type, before_value: before, after_value: after } = entry;
  if (type === "geometry_added") {
    return `Added a wall (${after?.material || "unbekannt"}, ${after?.thickness_mm ? `${after.thickness_mm.toFixed(0)}mm` : "?"})`;
  }
  if (type === "geometry_removed") {
    return `Removed a wall (${before?.material || "unbekannt"}, was flagged as a non-wall)`;
  }
  const label = { material: "Material", wall_type: "Wall type", thickness: "Thickness" }[type] || type;
  const fmt = (v) => (v === null || v === undefined || v === "" ? "—" : String(v));
  return `${label}: ${fmt(before)} → ${fmt(after)}`;
}

// ── Main editor ────────────────────────────────────────
// Full-screen "Blueprint Studio" shell around the floor-mask editor.
// The parent contract: getMaskPayload() returns {material_masks, added_walls}
// — one black/white mask per material layer (detected layer minus shared
// erase strokes, plus this layer's own vector walls baked in white, with
// other materials' walls punched out for exclusivity) plus exact thickness
// metadata for user-drawn walls. onPreview/onRecompute/onClose drive the
// same flows. Rooms and openings are annotation layers only — they never
// touch the exported masks.
const TakeoffImageEditor = forwardRef(function TakeoffImageEditor(
  {
    materialMasks,
    backgroundImageUrl,
    onFirstStroke,
    toolMode,
    onToolModeChange,
    floorLabel,
    floorId,
    pageIndex,
    isPreviewing,
    isRecomputing,
    errorMessage,
    onPreview,
    onRecompute,
    onClose,
    maskContext,
    previewImageUrl,
    onPreviewImageClick,
    availableThicknessesMm,
    measurements,
    measurementsStatus,
    overallConfidence,
    validation,
    lvComparison,
    onJumpToReason,
    pendingWallFocus,
    onWallFocused,
    jobId,
  },
  ref,
) {
  const svgRef = useRef(null);
  const canvasWrapRef = useRef(null);
  const shellRef = useRef(null);
  const layers = useMemo(() => materialMasks || [], [materialMasks]);
  const { images: layerImages, status: layerImagesStatus } = useLayerImages(layers);
  const [backgroundImage, backgroundImageStatus] = useImage(
    backgroundImageUrl,
    "anonymous",
  );

  // Vector annotation layers (walls / rooms / openings) in image coordinates.
  const [elements, setElements] = useState([]);
  // Freehand erase strokes over the detected mask: {points: flat [x,y,...], width}.
  // Shared across ALL material layers — erasing means "this is not a wall",
  // regardless of which material it was auto-assigned to.
  const [eraseStrokes, setEraseStrokes] = useState([]);
  const [currentErase, setCurrentErase] = useState(null); // null = not erasing
  const [history, setHistory] = useState([]);
  const [future, setFuture] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [draftPoints, setDraftPoints] = useState([]);
  const [drag, setDrag] = useState(null);
  const [brushPreview, setBrushPreview] = useState(null);
  const [showGrid, setShowGrid] = useState(true);
  const [gridSizeOverride, setGridSizeOverride] = useState(null);
  const [eraserSizeOverride, setEraserSizeOverride] = useState(null);
  const [viewportSize, setViewportSize] = useState({ width: 0, height: 0 });
  const [zoom, setZoom] = useState(1);
  // Which material the wall tool paints into, and which layers are hidden
  // (visual toggle only — hidden layers still export their content).
  const [activeMaterialKey, setActiveMaterialKey] = useState(
    () => layers[0]?.key ?? "unbekannt",
  );
  const [hiddenLayerKeys, setHiddenLayerKeys] = useState(() => new Set());
  // Selected EXISTING (auto-detected) wall — distinct from `selectedId`,
  // which selects a user-drawn `elements` item. Valid only for the current
  // render; never sent to the backend (wall ids are not stable across calls).
  const [selectedDetectedWallId, setSelectedDetectedWallId] = useState(null);
  // Hovered EXISTING (auto-detected) wall — read-only, drives the Measurements
  // panel's live readout only. Deliberately kept separate from
  // selectedDetectedWallId, whose Properties panel has an editable thickness
  // input: merging the two would risk showing/implying an editable field for
  // a wall the user only moused over, not explicitly selected.
  const [hoveredDetectedWallId, setHoveredDetectedWallId] = useState(null);
  // Pending thickness corrections for existing walls: [{bboxPct, layerKey,
  // thicknessMm}]. Referenced by position (bboxPct), not id, so a correction
  // still applies correctly even after the wall's id changes on the next
  // preview/recompute (see mask_layers.match_wall_thickness_overrides).
  const [thicknessOverrides, setThicknessOverrides] = useState([]);
  // Pending material/wall_type corrections for existing walls -- same
  // bbox_pct-referenced pattern as thicknessOverrides. A wall detected as
  // "unbekannt"/unresolved, or with the wrong wall_type, can be corrected in
  // place without deleting and redrawing it (see
  // mask_layers.match_wall_material_overrides / match_wall_type_overrides).
  const [materialOverrides, setMaterialOverrides] = useState([]);
  const [wallTypeOverrides, setWallTypeOverrides] = useState([]);
  const hasStrokedRef = useRef(false);
  const erasingRef = useRef(false);
  const dragMovedRef = useRef(false);
  const shouldCenterViewRef = useRef(false);
  const skipAutoPreviewRef = useRef(true);
  const autoPreviewTimerRef = useRef(null);
  // Coalesces resolveWallAt's linear scan (over every detected wall/erase
  // stroke) to once per animation frame instead of once per raw mousemove,
  // which can fire far faster than 60fps on a high-poll-rate mouse/trackpad.
  const pendingHoverPointRef = useRef(null);
  const hoverRafRef = useRef(null);
  // Edits made since this floor was opened that haven't survived a
  // successful recompute yet (preview is only a live estimate, never
  // persisted) — read by the parent via hasUnsavedEdits()/markSaved() to
  // warn before the editor closes and this state is discarded for good.
  const dirtyRef = useRef(false);
  // Flips once pushHistory has actually discarded the oldest entry to stay
  // within HISTORY_LIMIT, so the undo limit is surfaced instead of silently
  // capping how far back the user can go.
  const [historyTruncated, setHistoryTruncated] = useState(false);
  // Mask-render Worker plumbing (getMaskPayload — see below): lazily
  // created, terminated on unmount. maskWorkerRequestsRef correlates
  // postMessage responses back to the awaiting caller by id.
  const maskWorkerRef = useRef(null);
  const maskWorkerRequestsRef = useRef(new Map());
  const maskWorkerIdRef = useRef(0);
  // One decoded "master" ImageBitmap per layer image, self-invalidating —
  // keyed by the <img> element identity that useLayerImages only replaces
  // when a layer's URL actually changes. A fresh disposable clone of the
  // master is created per getMaskPayload() call for the Worker to consume
  // (postMessage transfer neuters the object it's given), so the master
  // itself survives for the next call.
  const masterBitmapsRef = useRef(new WeakMap());

  useEffect(() => {
    return () => {
      maskWorkerRef.current?.terminate();
      maskWorkerRef.current = null;
    };
  }, []);

  const sourceImage = Object.values(layerImages)[0] || backgroundImage;

  const nativeWidth = sourceImage
    ? sourceImage.naturalWidth || sourceImage.width || 1
    : 1;
  const nativeHeight = sourceImage
    ? sourceImage.naturalHeight || sourceImage.height || 1
    : 1;
  const bgWidth = backgroundImage?.naturalWidth || backgroundImage?.width || 0;
  const bgHeight =
    backgroundImage?.naturalHeight || backgroundImage?.height || 0;
  const bgIsMatchedReference = Boolean(sourceImage && maskContext?.reference_b64);
  const paintUpscale = Math.max(
    1,
    bgIsMatchedReference && bgWidth ? bgWidth / nativeWidth : 0,
    bgIsMatchedReference && bgHeight ? bgHeight / nativeHeight : 0,
    MIN_PAINT_DIM / Math.max(nativeWidth, nativeHeight),
  );
  const paintWidth = Math.max(1, Math.round(nativeWidth * paintUpscale));
  const paintHeight = Math.max(1, Math.round(nativeHeight * paintUpscale));

  // Per-image design scale: converts DESIGN_REF_DIM-space sizes to image px.
  const designScale = Math.max(paintWidth, paintHeight) / DESIGN_REF_DIM;
  const sz = useCallback((value) => value * designScale, [designScale]);

  // bbox_pct/hit_bbox_pct are percentages of the FULL PDF PAGE (masonry_detector.py:
  // pw, ph = page.rect.width, page.rect.height), but the on-screen background/mask
  // images are a CROPPED region of that page (context.reference_b64, rendered by
  // _render_reference_crop over (bx0,by0)-(bx0+nx*cell_pt, by0+ny*cell_pt)). Naively
  // treating a page-percent as a crop-percent silently misplaces every wall by an
  // amount that grows with distance from the crop origin. This maps page-percent ->
  // crop-relative-percent -> paint-space px, the missing middle step.
  const pagePctToPaintPx = useCallback(
    (xPct, yPct) => {
      const ctx = maskContext;
      const pw = ctx?.pw;
      const ph = ctx?.ph;
      const cellPt = ctx?.cell_pt;
      const maskShape = ctx?.mask_shape; // [ny, nx]
      if (!(pw > 0) || !(ph > 0) || !(cellPt > 0) || !Array.isArray(maskShape)) {
        // No crop context (older floor / native-line-pair path with no captured
        // mask geometry) - bbox_pct is already relative to the displayed image.
        return { x: (xPct / 100) * paintWidth, y: (yPct / 100) * paintHeight };
      }
      const bx0 = ctx?.bx0 ?? 0;
      const by0 = ctx?.by0 ?? 0;
      const [ny, nx] = maskShape;
      const cropWidthPt = nx * cellPt;
      const cropHeightPt = ny * cellPt;
      const xPt = (xPct / 100) * pw;
      const yPt = (yPct / 100) * ph;
      return {
        x: ((xPt - bx0) / cropWidthPt) * paintWidth,
        y: ((yPt - by0) / cropHeightPt) * paintHeight,
      };
    },
    [maskContext, paintWidth, paintHeight],
  );

  // Existing (auto-detected) walls, converted from page-percent to paint-space
  // px — the same coordinate space every other canvas element (user-drawn
  // walls, grid, erase strokes) already lives in. `x0/y0/x1/y1` (the hit-test
  // AND highlight-outline rectangle) comes from the backend's `hit_bbox_pct`
  // - already extended to exactly reach a real crossing group-mate at a
  // corner/T/X junction (see group_walls_by_material_adjacency in
  // wall_network.py) - falling back to `bbox_pct` when absent (older cached
  // data, or a floor with no captured mask geometry to derive it from), then
  // padded for on-screen click comfort. The wall's own unmodified `bbox_pct`
  // stays untouched on the spread object below for quantity math, the
  // Properties panel, and thickness-override writes.
  const detectedWalls = useMemo(() => {
    const walls = measurements?.walls;
    if (!Array.isArray(walls)) return [];
    const minHitPx = sz(MIN_WALL_HIT_PX);
    const edgePad = sz(WALL_HIT_EDGE_PAD_PX);
    return walls
      .filter((w) => Array.isArray(w.bbox_pct) && w.bbox_pct.length === 4)
      .map((w) => {
        const [hx0, hy0, hx1, hy1] = Array.isArray(w.hit_bbox_pct) && w.hit_bbox_pct.length === 4
          ? w.hit_bbox_pct
          : w.bbox_pct;
        const p0 = pagePctToPaintPx(hx0, hy0);
        const p1 = pagePctToPaintPx(hx1, hy1);
        let px0 = p0.x;
        let py0 = p0.y;
        let px1 = p1.x;
        let py1 = p1.y;
        if (Math.abs(px1 - px0) < minHitPx) {
          const cx = (px0 + px1) / 2;
          px0 = cx - minHitPx / 2;
          px1 = cx + minHitPx / 2;
        }
        if (Math.abs(py1 - py0) < minHitPx) {
          const cy = (py0 + py1) / 2;
          py0 = cy - minHitPx / 2;
          py1 = cy + minHitPx / 2;
        }
        px0 -= edgePad;
        py0 -= edgePad;
        px1 += edgePad;
        py1 += edgePad;
        return {
          ...w,
          x0: Math.min(px0, px1),
          y0: Math.min(py0, py1),
          x1: Math.max(px0, px1),
          y1: Math.max(py0, py1),
        };
      });
  }, [measurements, sz, pagePctToPaintPx]);

  // Every WallRecord is constructed with a real bbox_pct server-side (required
  // field, never defaulted) - the only way one goes missing here is stale
  // cached data predating some schema field, or a corrupted reload. Rather
  // than let detectedWalls' own filter (above) silently drop such an entry
  // with zero trace, count it so the editor can say so instead of leaving a
  // validation message pointing at a wall nobody can find.
  const undisplayableWallCount = useMemo(() => {
    const walls = measurements?.walls;
    if (!Array.isArray(walls)) return 0;
    return walls.filter((w) => !Array.isArray(w.bbox_pct) || w.bbox_pct.length !== 4).length;
  }, [measurements]);

  // Resolves a validation reason's bbox_pct (same-session data, no recompute
  // in between) back to a wall in THIS floor's detectedWalls - a small
  // position tolerance absorbs any float round-trip noise, not a fuzzy match.
  const findWallByBboxPct = useCallback(
    (bboxPct) => {
      if (!Array.isArray(bboxPct) || bboxPct.length !== 4) return null;
      return (
        detectedWalls.find(
          (w) =>
            Array.isArray(w.bbox_pct) &&
            w.bbox_pct.length === 4 &&
            w.bbox_pct.every((v, i) => Math.abs(v - bboxPct[i]) < 0.01),
        ) || null
      );
    },
    [detectedWalls],
  );

  // Clicking a validation reason with a source_page can land on a different
  // floor than the one currently open - TakeoffWorkflow switches floors first
  // (handleOpenFloorInEditor) and hands the target position down via this
  // prop; once THIS floor's pageIndex/detectedWalls reflect that switch, pick
  // the matching wall and hand control back to the parent (onWallFocused)
  // so a stale pendingWallFocus can't re-fire on the next unrelated render.
  useEffect(() => {
    if (!pendingWallFocus || pendingWallFocus.source_page !== pageIndex) return;
    if (pendingWallFocus.bbox_pct) {
      const match = findWallByBboxPct(pendingWallFocus.bbox_pct);
      if (match) setSelectedDetectedWallId(match.id);
    }
    onWallFocused?.();
  }, [pendingWallFocus, pageIndex, findWallByBboxPct, onWallFocused]);

  // Change-history panel: fetched lazily on first expand (job-scoped, not
  // floor-scoped -- covers every floor's corrections, agent and human).
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyEntries, setHistoryEntries] = useState(null); // null = not loaded yet
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState(null);
  const toggleHistory = useCallback(() => {
    setHistoryOpen((prev) => !prev);
  }, []);
  useEffect(() => {
    if (!historyOpen || historyEntries !== null || !jobId) return;
    let cancelled = false;
    setHistoryLoading(true);
    setHistoryError(null);
    fetch(`${API_BASE_URL}/api/v1/construction/wall-extraction/${jobId}/history`)
      .then((res) => {
        if (!res.ok) throw new Error("Failed to load change history");
        return res.json();
      })
      .then((data) => {
        if (!cancelled) setHistoryEntries(data.entries || []);
      })
      .catch((err) => {
        if (!cancelled) setHistoryError(err.message);
      })
      .finally(() => {
        if (!cancelled) setHistoryLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [historyOpen, historyEntries, jobId]);

  // A wall run that turns a corner (L/T-junction) is never merged into one
  // bbox server-side (that would corrupt hit-testing/quantity math for a
  // non-rectangular run) - instead same-material touching walls share a
  // group_id, and every rect in the group gets the same highlight so the
  // whole run reads as one selectable unit. Falls back to the wall's own id
  // when group_id is absent (older cached data, or a floor with no captured
  // mask geometry to derive grouping from) - identical to today's behavior.
  const groupKeyOf = useCallback(
    (wall) => (wall ? wall.group_id || wall.id : null),
    [],
  );

  // "Worth a second look" is decided entirely server-side (wall_needs_review,
  // validation.py) and shipped as needs_review/review_reason on every wall
  // dict (_wall_list_for_display) - the frontend must only READ these, never
  // re-derive its own proxy (a prior version checked material_method/
  // provenance directly here, which silently drifted from the backend's
  // actual definition). Both fields are absent (undefined) on live-preview
  // wall dicts (preview uses a different, classification-free code path) -
  // Boolean(undefined) / undefined-or-"" naturally treat that as "no signal".
  const needsReview = useCallback((wall) => Boolean(wall?.needs_review), []);
  const flaggedWalls = useMemo(
    () => detectedWalls.filter(needsReview),
    [detectedWalls, needsReview],
  );
  const flaggedWallCount = flaggedWalls.length;
  // Position of the currently-selected wall within flaggedWalls (-1 if the
  // selection isn't a flagged wall, or nothing is selected) - drives both the
  // "N of M unresolved" label and where "Next unresolved" resumes from, so
  // clicking a flagged wall directly on the canvas and then clicking the
  // button continues from THAT wall rather than restarting at the first one.
  const flaggedWallCursor = useMemo(
    () => flaggedWalls.findIndex((w) => w.id === selectedDetectedWallId),
    [flaggedWalls, selectedDetectedWallId],
  );
  // Selecting by id directly (not by hit-testing a canvas point) sidesteps
  // resolveWallAt's smallest-area tie-break, which can otherwise make one of
  // two overlapping walls at a junction unreachable by click.
  const handleSelectNextUnresolved = useCallback(() => {
    if (flaggedWalls.length === 0) return;
    const next = (flaggedWallCursor + 1) % flaggedWalls.length;
    setSelectedDetectedWallId(flaggedWalls[next].id);
  }, [flaggedWalls, flaggedWallCursor]);
  // Drives the Data quality panel's subtitle/content: when nothing below the
  // badge would actually render, the panel needs to say so explicitly rather
  // than silently showing just a title+badge, which reads as broken/empty.
  const hasDataQualityIssue =
    Boolean(validation?.escalated) ||
    (validation?.flags?.length ?? 0) > 0 ||
    (lvComparison?.some((g) => g.status !== "ok") ?? false) ||
    flaggedWallCount > 0 ||
    undisplayableWallCount > 0;
  const reviewReason = useCallback((wall) => wall?.review_reason || "", []);
  const selectedGroupKey = useMemo(
    () => groupKeyOf(detectedWalls.find((w) => w.id === selectedDetectedWallId)),
    [detectedWalls, selectedDetectedWallId, groupKeyOf],
  );
  const hoveredGroupKey = useMemo(
    () => groupKeyOf(detectedWalls.find((w) => w.id === hoveredDetectedWallId)),
    [detectedWalls, hoveredDetectedWallId, groupKeyOf],
  );

  // Erase strokes are shared across ALL material layers ("erasing means this
  // is not a wall, regardless of material") and already live in paint-space
  // px, same as detectedWalls' hit rectangles - a plain point-to-segment
  // distance check, no rasterization needed.
  const isErasedAt = useCallback(
    (paintX, paintY) => {
      for (const stroke of eraseStrokes) {
        const pts = stroke.points || [];
        const halfWidth = (stroke.width || 0) / 2;
        if (pts.length < 2) continue;
        if (pts.length === 2) {
          if (distance({ x: paintX, y: paintY }, { x: pts[0], y: pts[1] }) <= halfWidth) return true;
          continue;
        }
        for (let i = 0; i + 3 < pts.length; i += 2) {
          if (pointToSegmentDistance(paintX, paintY, pts[i], pts[i + 1], pts[i + 2], pts[i + 3]) <= halfWidth) {
            return true;
          }
        }
      }
      return false;
    },
    [eraseStrokes],
  );

  // Resolve a paint-space point to a detected-wall id: erased beats
  // everything (explicit "not a wall"), then the smallest-area wall whose
  // (backend-exact, comfort-padded) hit rectangle contains the point wins -
  // two grouped walls legitimately overlap at a real junction, and either is
  // "correct" there, so the smaller/more-specific one is just a sensible
  // tie-break, not a correctness requirement.
  const resolveWallAt = useCallback(
    (paintX, paintY) => {
      if (isErasedAt(paintX, paintY)) return null;
      let bestId = null;
      let bestArea = Infinity;
      for (const w of detectedWalls) {
        if (hiddenLayerKeys.has(w.layer_key)) continue;
        if (paintX < w.x0 || paintX > w.x1 || paintY < w.y0 || paintY > w.y1) continue;
        const area = (w.x1 - w.x0) * (w.y1 - w.y0);
        if (area < bestArea) {
          bestArea = area;
          bestId = w.id;
        }
      }
      return bestId;
    },
    [isErasedAt, detectedWalls, hiddenLayerKeys],
  );

  // Distinct thicknesses actually detected per material (from the live wall
  // list), so the "wall" tool's presets reflect what THIS material really
  // has on the plan instead of one flat list mixing every material together.
  const thicknessesByLayer = useMemo(() => {
    const byLayer = {};
    for (const w of detectedWalls) {
      if (!w.layer_key || !Number.isFinite(w.thickness_mm) || w.thickness_mm <= 0) continue;
      (byLayer[w.layer_key] ??= new Set()).add(w.thickness_mm);
    }
    return byLayer;
  }, [detectedWalls]);

  // Same fuzzy-position matching idea the backend uses (mask_layers.
  // match_wall_thickness_overrides): replace an existing pending override for
  // (roughly) the same wall instead of accumulating duplicates.
  function upsertThicknessOverride(bboxPct, layerKey, thicknessMm) {
    const closeEnough = (a, b) =>
      a.every((v, i) => Math.abs(v - b[i]) < 0.5); // page-percent tolerance
    setThicknessOverrides((prev) => {
      const next = prev.filter((o) => !closeEnough(o.bboxPct, bboxPct));
      next.push({ bboxPct, layerKey, thicknessMm });
      return next;
    });
  }

  function upsertMaterialOverride(bboxPct, layerKey) {
    const closeEnough = (a, b) => a.every((v, i) => Math.abs(v - b[i]) < 0.5);
    setMaterialOverrides((prev) => {
      const next = prev.filter((o) => !closeEnough(o.bboxPct, bboxPct));
      next.push({ bboxPct, layerKey });
      return next;
    });
  }

  function upsertWallTypeOverride(bboxPct, wallType) {
    const closeEnough = (a, b) => a.every((v, i) => Math.abs(v - b[i]) < 0.5);
    setWallTypeOverrides((prev) => {
      const next = prev.filter((o) => !closeEnough(o.bboxPct, bboxPct));
      next.push({ bboxPct, wallType });
      return next;
    });
  }

  // Wall-thickness presets: prefer THIS material's own detected thicknesses
  // (real mm), falling back to the floor-wide flat list, then an
  // env-configurable generic list when nothing was detected yet.
  const presetsMm = useMemo(() => {
    const perMaterial = thicknessesByLayer[activeMaterialKey];
    const detected = perMaterial?.size
      ? [...perMaterial]
      : (availableThicknessesMm || []).filter(
          (value) => Number.isFinite(value) && value > 0,
        );
    const list = detected.length ? detected : DEFAULT_THICKNESS_PRESETS_MM;
    return [...new Set(list)].sort((a, b) => a - b);
  }, [availableThicknessesMm, thicknessesByLayer, activeMaterialKey]);

  // Real-world mm -> paint-space px. context.mm_per_px (mm per one native mask
  // pixel) is computed backend-side with the exact same formula used for
  // auto-detected wall thickness, so a wall drawn at a chosen mm preset
  // rasterizes to the same pixel width a real wall of that thickness would.
  // Falls back to the old fixed design-space thickness for floors whose
  // context predates this field.
  const mmToPaintPx = useCallback(
    (mm) => {
      const mmPerPx = maskContext?.mm_per_px;
      if (mmPerPx > 0) {
        return Math.max(1, Math.round((mm / mmPerPx) * paintUpscale));
      }
      return Math.round(sz(DESIGN.wallThickness));
    },
    [maskContext, paintUpscale, sz],
  );

  const presetPx = useMemo(
    () => presetsMm.map(mmToPaintPx),
    [presetsMm, mmToPaintPx],
  );

  // Inverse of mmToPaintPx: paint-space px -> real-world mm, so dragging the
  // per-wall thickness slider keeps an accurate thickness_mm for added_walls
  // (raster-only pixels are too coarse — see getMaskPayload). Returns null
  // when the floor's context predates mm_per_px (legacy — thickness_mm stays
  // whatever it already was; the backend geometric fallback covers it).
  const paintPxToMm = useCallback(
    (px) => {
      const mmPerPx = maskContext?.mm_per_px;
      if (mmPerPx > 0) return (px / paintUpscale) * mmPerPx;
      return null;
    },
    [maskContext, paintUpscale],
  );

  const [selectedThicknessMm, setSelectedThicknessMm] = useState(
    () => presetsMm[0],
  );

  const gridSize = gridSizeOverride ?? Math.max(1, Math.round(sz(DESIGN.gridDefault)));
  const eraserSize =
    eraserSizeOverride ?? Math.max(1, Math.round(sz(DESIGN.eraserDefault)));
  const eraserMin = Math.max(1, Math.round(sz(DESIGN.eraserMin)));
  const eraserMax = Math.max(eraserMin + 1, Math.round(sz(DESIGN.eraserMax)));
  const gridMin = Math.max(1, Math.round(sz(DESIGN.gridMin)));
  const gridMax = Math.max(gridMin + 1, Math.round(sz(DESIGN.gridMax)));
  const thicknessMin = Math.max(
    1,
    Math.min(Math.round(sz(DESIGN.wallThicknessMin)), ...presetPx),
  );
  const thicknessMax = Math.max(
    thicknessMin + 1,
    Math.round(sz(DESIGN.wallThicknessMax)),
    ...presetPx,
  );
  const openingMin = Math.max(1, Math.round(sz(DESIGN.openingWidthMin)));
  const openingMax = Math.max(
    openingMin + 1,
    Math.round(sz(DESIGN.openingWidthMax)),
  );
  const wallMergeTolerance = useMemo(
    () => ({ gap: sz(DESIGN.wallMergeGap), line: sz(DESIGN.wallMergeLineDistance) }),
    [sz],
  );

  const layerColorByKey = useMemo(
    () => Object.fromEntries(layers.map((layer) => [layer.key, layer.color])),
    [layers],
  );
  const wallStrokeColor = (wall) =>
    layerColorByKey[wall.materialKey] || DETECTED_WALL_COLOR;

  const selectedDetectedWall = detectedWalls.find(
    (w) => w.id === selectedDetectedWallId,
  );
  const hoveredDetectedWall = detectedWalls.find(
    (w) => w.id === hoveredDetectedWallId,
  );
  // Properties panel follows the mouse like the Measurements panel already
  // does — shows whatever's hovered, falling back to the selected wall once
  // hover ends. The thickness field itself stays gated to a true selection
  // (see the Properties panel render below) so hovering never implies an
  // edit is happening to a wall the user hasn't explicitly clicked.
  const displayedDetectedWall = hoveredDetectedWall || selectedDetectedWall;

  // Pending material/wall_type override drafts for the SELECTED wall (if
  // any) -- so the dropdown reflects the just-picked value, not the
  // original detection, until Save & Recompute is clicked.
  const closeEnoughBbox = (a, b) => a.every((v, i) => Math.abs(v - b[i]) < 0.5);
  const pendingMaterialOverride = selectedDetectedWall
    ? materialOverrides.find((o) => closeEnoughBbox(o.bboxPct, selectedDetectedWall.bbox_pct))
    : undefined;
  const pendingWallTypeOverride = selectedDetectedWall
    ? wallTypeOverrides.find((o) => closeEnoughBbox(o.bboxPct, selectedDetectedWall.bbox_pct))
    : undefined;

  // The parent owns toolMode; normalize legacy brush values ("add"/"remove")
  // so previously stored modes still resolve to a valid tool.
  const tool = TOOLS.includes(toolMode)
    ? toolMode
    : toolMode === "remove"
      ? "erase"
      : "select";

  // The material chip list has exactly one highlight, whose source depends on
  // which tool is active — detected-wall hover/select only ever updates in
  // Select mode (see handleDetectedWallLayerPointerMove/Click below, both
  // gated on tool === "select"), and the paint-target material only matters
  // while actively drawing with the Wall tool. Never two sources competing
  // for the same highlight at once, and switching tools can't leave a stale
  // highlight behind from whichever concept applied under the previous tool.
  const chipHighlightKey =
    tool === "select" ? displayedDetectedWall?.layer_key : activeMaterialKey;

  const walls = elements.filter((item) => item.type === "wall");
  const rooms = elements.filter((item) => item.type === "room");
  const openings = elements.filter((item) => item.type === "opening");
  const selected = elements.find((item) => item.id === selectedId);

  useEffect(() => {
    const wrap = canvasWrapRef.current;
    if (!wrap) return;

    // Measure the content box (padding excluded) so the fitted stage is
    // never larger than the visible area and stays centered at 100% zoom.
    const updateSize = () => {
      const style = getComputedStyle(wrap);
      const padX =
        parseFloat(style.paddingLeft) + parseFloat(style.paddingRight);
      const padY =
        parseFloat(style.paddingTop) + parseFloat(style.paddingBottom);
      const rect = wrap.getBoundingClientRect();
      setViewportSize({
        width: Math.max(1, Math.floor(rect.width - padX)),
        height: Math.max(1, Math.floor(rect.height - padY)),
      });
    };

    updateSize();
    const observer = new ResizeObserver(updateSize);
    observer.observe(wrap);
    return () => observer.disconnect();
  }, []);

  const fitWidth = Math.max(1, viewportSize.width - STAGE_FIT_MARGIN);
  const fitHeight = Math.max(1, viewportSize.height - STAGE_FIT_MARGIN);
  const fitScale = sourceImage
    ? Math.min(fitWidth / paintWidth, fitHeight / paintHeight)
    : 1;
  const stageWidth = Math.max(1, Math.round(paintWidth * fitScale * zoom));
  const stageHeight = Math.max(1, Math.round(paintHeight * fitScale * zoom));
  const showStage =
    layerImagesStatus === "loaded" || backgroundImageStatus === "loaded";

  const centerViewportOnStage = useCallback(() => {
    const wrap = canvasWrapRef.current;
    if (!wrap) return;

    const maxScrollLeft = Math.max(0, stageWidth - wrap.clientWidth);
    const maxScrollTop = Math.max(0, stageHeight - wrap.clientHeight);
    wrap.scrollLeft = maxScrollLeft / 2;
    wrap.scrollTop = maxScrollTop / 2;
  }, [stageHeight, stageWidth]);

  // Reset the whole editing session whenever a different floor mask is opened.
  useEffect(() => {
    setZoom(1);
    setElements([]);
    setEraseStrokes([]);
    setCurrentErase(null);
    setHistory([]);
    setFuture([]);
    setSelectedId(null);
    setDraftPoints([]);
    setDrag(null);
    setBrushPreview(null);
    setGridSizeOverride(null);
    setEraserSizeOverride(null);
    setSelectedThicknessMm(presetsMm[0]);
    setActiveMaterialKey(layers[0]?.key ?? "unbekannt");
    setHiddenLayerKeys(new Set());
    setSelectedDetectedWallId(null);
    setThicknessOverrides([]);
    setMaterialOverrides([]);
    setWallTypeOverrides([]);
    shouldCenterViewRef.current = true;
    erasingRef.current = false;
    hasStrokedRef.current = false;
    skipAutoPreviewRef.current = true;
    dirtyRef.current = false;
    setHistoryTruncated(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [floorId]);

  useEffect(() => {
    if (!showStage || !shouldCenterViewRef.current) return;
    const frame = requestAnimationFrame(() => {
      centerViewportOnStage();
      shouldCenterViewRef.current = false;
    });
    return () => cancelAnimationFrame(frame);
  }, [centerViewportOnStage, showStage]);

  // Auto-estimate: after any mask-affecting edit (wall add/move/delete, erase
  // stroke, thickness change), fire a debounced preview so the measurements
  // panel refreshes near-live without re-running the slow full recompute.
  // Skips the reset triggered by opening a floor (nothing to estimate yet).
  //
  // skipAutoPreviewRef alone isn't enough: the floor-open reset sets
  // elements/eraseStrokes/thicknessOverrides to NEW (still logically empty)
  // array references, which this effect's shallow dependency comparison sees
  // as "changed" a second time once those state updates actually land - by
  // then the skip flag has already been consumed by this effect's first
  // (correctly-skipped) run, so that second run used to slip through and
  // fire an unwanted preview ~800ms after every floor open, silently
  // replacing the properly-computed persisted wall list with an
  // independently re-enumerated live one (ids/groups are not stable across
  // calls) even though the user never made any edit. isPristine catches
  // both runs: neither has a real edit in it yet.
  useEffect(() => {
    if (skipAutoPreviewRef.current) {
      skipAutoPreviewRef.current = false;
      return undefined;
    }
    const isPristine =
      elements.length === 0 && eraseStrokes.length === 0 && thicknessOverrides.length === 0
      && materialOverrides.length === 0 && wallTypeOverrides.length === 0;
    dirtyRef.current = !isPristine;
    if (isPristine) return undefined;
    if (autoPreviewTimerRef.current) clearTimeout(autoPreviewTimerRef.current);
    autoPreviewTimerRef.current = setTimeout(() => {
      if (!isRecomputing) onPreview?.();
    }, PREVIEW_DEBOUNCE_MS);
    return () => clearTimeout(autoPreviewTimerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [elements, eraseStrokes, thicknessOverrides, materialOverrides, wallTypeOverrides]);

  // ── History (undo/redo over vector layers + erase strokes) ──
  function snapshot() {
    return { elements: clone(elements), eraseStrokes: clone(eraseStrokes) };
  }

  function applySnapshot(state) {
    setElements(clone(state.elements));
    setEraseStrokes(clone(state.eraseStrokes));
  }

  function pushHistory(previous = snapshot()) {
    setHistory((items) => {
      if (items.length >= HISTORY_LIMIT) setHistoryTruncated(true);
      return [...items.slice(-(HISTORY_LIMIT - 1)), previous];
    });
    setFuture([]);
  }

  function commit(mutator) {
    const draft = snapshot();
    mutator(draft);
    pushHistory();
    setElements(draft.elements);
    setEraseStrokes(draft.eraseStrokes);
  }

  function markMaskEdited() {
    if (hasStrokedRef.current) return;
    hasStrokedRef.current = true;
    onFirstStroke?.();
  }

  function undo() {
    if (!history.length) return;
    const previous = history[history.length - 1];
    setFuture((items) => [snapshot(), ...items]);
    setHistory((items) => items.slice(0, -1));
    applySnapshot(previous);
    setSelectedId(null);
    setDraftPoints([]);
  }

  function redo() {
    if (!future.length) return;
    const next = future[0];
    setHistory((items) => [...items.slice(-(HISTORY_LIMIT - 1)), snapshot()]);
    setFuture((items) => items.slice(1));
    applySnapshot(next);
    setSelectedId(null);
    setDraftPoints([]);
  }

  useEffect(() => {
    function isEditableTarget(event) {
      const tag = event.target.tagName;
      return tag === "INPUT" || tag === "TEXTAREA" || event.target.isContentEditable;
    }

    function handleKeyDown(event) {
      // Never steal keystrokes from a focused text/number field — Ctrl+Z
      // should undo a text edit there, not the canvas, and Backspace/Delete
      // should erase a character, not the selected element.
      if (isEditableTarget(event)) return;

      const isUndo =
        (event.metaKey || event.ctrlKey) &&
        event.key.toLowerCase() === "z" &&
        !event.shiftKey;
      const isRedo =
        (event.metaKey || event.ctrlKey) &&
        event.key.toLowerCase() === "z" &&
        event.shiftKey;
      if (isUndo || isRedo) {
        event.preventDefault();
        if (isUndo) undo();
        if (isRedo) redo();
        return;
      }

      if ((event.key === "Delete" || event.key === "Backspace") && selectedId) {
        event.preventDefault();
        deleteSelected();
      }
    }

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  });

  // Full-screen dialog semantics: this component is conditionally MOUNTED
  // by the parent (open = mount, close = unmount), so a plain mount/unmount
  // effect matches the dialog's open/close lifecycle exactly. Restores
  // focus to whatever triggered the open, traps Tab/Shift+Tab inside the
  // dialog (the Completed section, including the floors grid, stays
  // mounted behind this full-screen editor, so an untrapped Tab could walk
  // a keyboard user straight into it), and Escape closes it the same way
  // the Close button does.
  useEffect(() => {
    const shell = shellRef.current;
    if (!shell) return undefined;
    const previouslyFocused = document.activeElement;

    const FOCUSABLE_SELECTOR =
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
    function getFocusable() {
      return Array.from(shell.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
        (el) => el.offsetParent !== null,
      );
    }

    const initial = getFocusable();
    (initial[0] || shell).focus();

    function handleDialogKeyDown(event) {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose?.();
        return;
      }
      if (event.key !== "Tab") return;
      const focusable = getFocusable();
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    // Listens on document (not the shell) so Escape still closes the dialog
    // even if a click landed on a non-focusable element and shifted
    // document.activeElement outside the shell — matches the existing
    // image-lightbox's Escape convention (TakeoffWorkflow.jsx).
    document.addEventListener("keydown", handleDialogKeyDown);
    return () => {
      document.removeEventListener("keydown", handleDialogKeyDown);
      previouslyFocused?.focus?.();
    };
  }, [onClose]);

  // ── Coordinates ────────────────────────────────────────
  function clientToWorld(event) {
    const svg = svgRef.current;
    if (!svg) return { x: 0, y: 0 };
    const rect = svg.getBoundingClientRect();
    return {
      x: ((event.clientX - rect.left) / Math.max(1, rect.width)) * paintWidth,
      y: ((event.clientY - rect.top) / Math.max(1, rect.height)) * paintHeight,
    };
  }

  function setTool(nextTool) {
    onToolModeChange?.(nextTool);
    setDraftPoints([]);
  }

  // ── Erase (drag paints out the detected mask; click deletes layers) ──
  function finalizeEraseStroke() {
    if (!erasingRef.current) return;
    erasingRef.current = false;
    const points = currentErase;
    setCurrentErase(null);
    if (!points || points.length < 2) return;
    const stroke = {
      points: ensureRenderableStroke(points),
      width: eraserSize,
    };
    commit((draft) => draft.eraseStrokes.push(stroke));
    markMaskEdited();
  }

  function handleCanvasPointerDown(event) {
    if (tool !== "erase") return;
    if (event.target.closest("[data-element-id]")) return;
    const point = clientToWorld(event);
    setBrushPreview(point);
    if (!Object.keys(layerImages).length) return;
    event.preventDefault();
    erasingRef.current = true;
    setCurrentErase([point.x, point.y]);
  }

  function handlePointerMove(event) {
    if (tool === "erase") {
      const point = clientToWorld(event);
      setBrushPreview(point);
      if (erasingRef.current) {
        setCurrentErase((prev) =>
          prev ? [...prev, point.x, point.y] : [point.x, point.y],
        );
      }
      return;
    }
    if (!drag) return;
    const point = clientToWorld(event);
    const dx = point.x - drag.start.x;
    const dy = point.y - drag.start.y;
    if (Math.abs(dx) > 1 || Math.abs(dy) > 1) dragMovedRef.current = true;
    const nextElements = clone(drag.baseElements);
    const item = nextElements.find(
      (element) => element.id === drag.elementId,
    );
    if (!item) return;

    if (drag.handleIndex !== null) {
      if (item.type === "wall")
        item.points[drag.handleIndex] = {
          x: drag.original.points[drag.handleIndex].x + dx,
          y: drag.original.points[drag.handleIndex].y + dy,
        };
      if (item.type === "room")
        item.polygon[drag.handleIndex] = {
          x: drag.original.polygon[drag.handleIndex].x + dx,
          y: drag.original.polygon[drag.handleIndex].y + dy,
        };
      if (item.type === "opening") {
        const rotation = (drag.original.rotation * Math.PI) / 180;
        const localX =
          (point.x - drag.original.x) * Math.cos(rotation) +
          (point.y - drag.original.y) * Math.sin(rotation);
        item.width = Math.max(openingMin, Math.abs(localX) * 2);
      }
    } else if (item.type === "opening") {
      item.x = drag.original.x + dx;
      item.y = drag.original.y + dy;
    } else {
      const key = item.type === "wall" ? "points" : "polygon";
      item[key] = drag.original[key].map((p) => ({ x: p.x + dx, y: p.y + dy }));
    }

    setElements(nextElements);
  }

  function stopDrag() {
    if (!drag) return;
    if (dragMovedRef.current) {
      pushHistory({
        elements: drag.baseElements,
        eraseStrokes: drag.baseEraseStrokes,
      });
      if (drag.affectsMask) markMaskEdited();
    }
    dragMovedRef.current = false;
    setDrag(null);
  }

  function handlePointerUp() {
    finalizeEraseStroke();
    stopDrag();
  }

  function handleCanvasLeave() {
    setBrushPreview(null);
    finalizeEraseStroke();
    stopDrag();
  }

  function eraseElement(elementId) {
    const wasWall = elements.some(
      (item) => item.id === elementId && item.type === "wall",
    );
    commit((draft) => {
      draft.elements = draft.elements.filter((item) => item.id !== elementId);
    });
    if (wasWall) markMaskEdited();
    if (selectedId === elementId) setSelectedId(null);
    setDraftPoints([]);
  }

  // ── Drawing tools (canvas.jsx interactions) ────────────
  function handleCanvasClick(event) {
    const elementNode = event.target.closest("[data-element-id]");
    // Erase-by-click on an element is already handled by startElementDrag's
    // onPointerDown (which stops propagation of the pointerdown, but not of
    // the separately-dispatched click that follows) — handling it again here
    // would erase the same element twice and duplicate the undo history.
    if (tool === "erase") return;
    if (elementNode) return;
    if (event.target.closest("[data-detected-wall-id]")) return;
    const point = clientToWorld(event);

    if (tool === "wall") {
      if (!draftPoints.length) {
        setDraftPoints([point]);
      } else {
        const wall = {
          id: uid("w"),
          type: "wall",
          wallType: "inner",
          points: [draftPoints[0], point],
          thickness: mmToPaintPx(selectedThicknessMm),
          thicknessMm: selectedThicknessMm,
          materialKey: activeMaterialKey,
        };
        let nextSelectedId = wall.id;
        commit((draft) => {
          nextSelectedId = upsertWall(draft.elements, wall, wallMergeTolerance);
        });
        markMaskEdited();
        setDraftPoints([]);
        setSelectedId(nextSelectedId);
        setSelectedDetectedWallId(null);
        setTool("select");
      }
      return;
    }

    if (tool === "room") {
      const nextDraft = [...draftPoints, point];
      if (
        nextDraft.length >= 4 &&
        distance(nextDraft[0], point) < sz(DESIGN.roomCloseDistance)
      ) {
        const polygon = nextDraft.slice(0, -1);
        const room = {
          id: uid("r"),
          type: "room",
          name: `Room ${rooms.length + 1}`,
          color: ROOM_COLORS[rooms.length % ROOM_COLORS.length],
          polygon,
        };
        commit((draft) => draft.elements.push(room));
        setDraftPoints([]);
        setSelectedId(room.id);
        setSelectedDetectedWallId(null);
        setTool("select");
      } else {
        setDraftPoints(nextDraft);
      }
      return;
    }

    if (tool === "opening") {
      const opening = {
        id: uid("o"),
        type: "opening",
        name: `Opening ${openings.length + 1}`,
        x: point.x,
        y: point.y,
        rotation: 0,
        width: Math.round(sz(DESIGN.openingWidth)),
      };
      commit((draft) => draft.elements.push(opening));
      setSelectedId(opening.id);
      setSelectedDetectedWallId(null);
      setTool("select");
      return;
    }

    setSelectedId(null);
    setSelectedDetectedWallId(null);
  }

  function handleDetectedWallLayerPointerMove(event) {
    if (tool !== "select") return;
    pendingHoverPointRef.current = clientToWorld(event);
    if (hoverRafRef.current != null) return;
    hoverRafRef.current = requestAnimationFrame(() => {
      hoverRafRef.current = null;
      const point = pendingHoverPointRef.current;
      if (!point) return;
      const hitId = resolveWallAt(point.x, point.y);
      setHoveredDetectedWallId((current) => (current === hitId ? current : hitId));
    });
  }

  function handleDetectedWallLayerMouseLeave() {
    pendingHoverPointRef.current = null;
    if (hoverRafRef.current != null) {
      cancelAnimationFrame(hoverRafRef.current);
      hoverRafRef.current = null;
    }
    setHoveredDetectedWallId(null);
  }

  useEffect(() => {
    return () => {
      if (hoverRafRef.current != null) cancelAnimationFrame(hoverRafRef.current);
    };
  }, []);

  function handleDetectedWallLayerClick(event) {
    if (tool !== "select") return;
    const { x, y } = clientToWorld(event);
    const hitId = resolveWallAt(x, y);
    // No hit: don't stop propagation - let the click bubble up to
    // handleCanvasClick's existing deselect fallback, same as a genuine
    // blank-canvas click always has.
    if (!hitId) return;
    event.stopPropagation();
    setSelectedId(null);
    setSelectedDetectedWallId(hitId);
  }

  function startElementDrag(event, elementId, handleIndex = null) {
    if (tool === "erase") {
      event.stopPropagation();
      eraseElement(elementId);
      return;
    }
    if (tool !== "select") return;
    event.stopPropagation();
    const start = clientToWorld(event);
    const element = elements.find((item) => item.id === elementId);
    if (!element) return;
    dragMovedRef.current = false;
    setSelectedId(elementId);
    setSelectedDetectedWallId(null);
    setDrag({
      elementId,
      handleIndex,
      start,
      baseElements: clone(elements),
      baseEraseStrokes: clone(eraseStrokes),
      original: clone(element),
      affectsMask: element.type === "wall",
    });
  }

  // ── Selected-element editing ───────────────────────────
  function updateSelected(patch, affectsMask = false) {
    if (!selectedId) return;
    commit((draft) => {
      const item = draft.elements.find(
        (element) => element.id === selectedId,
      );
      if (item) Object.assign(item, patch);
    });
    if (affectsMask) markMaskEdited();
  }

  function deleteSelected() {
    if (!selectedId) return;
    if (!window.confirm(`Delete this ${selected?.type || "element"}?`)) return;
    eraseElement(selectedId);
  }

  function duplicateSelected() {
    if (!selected) return;
    const offset = sz(DESIGN.duplicateOffset);
    const copy = clone(selected);
    copy.id = uid(selected.type[0]);
    if (copy.type === "room") {
      copy.name = `${copy.name} Copy`;
      copy.polygon = copy.polygon.map((p) => ({
        x: p.x + offset,
        y: p.y + offset,
      }));
    }
    if (copy.type === "wall") {
      copy.points = copy.points.map((p) => ({
        x: p.x + offset,
        y: p.y + offset,
      }));
    }
    if (copy.type === "opening") {
      copy.x += offset;
      copy.y += offset;
    }
    commit((draft) => draft.elements.push(copy));
    if (copy.type === "wall") markMaskEdited();
    setSelectedId(copy.id);
  }

  function clearLayers() {
    if (!elements.length) return;
    if (
      !window.confirm(
        `Clear all ${elements.length} drawn element(s) from this floor? You can undo this with Ctrl+Z until you close the editor.`,
      )
    )
      return;
    commit((draft) => {
      draft.elements = [];
    });
    setSelectedId(null);
    setDraftPoints([]);
  }

  function clearEraseStrokes() {
    if (!eraseStrokes.length) return;
    if (
      !window.confirm(
        `Clear all ${eraseStrokes.length} erase stroke(s)? This restores the original detected mask in those areas (undo with Ctrl+Z until you close the editor).`,
      )
    )
      return;
    commit((draft) => {
      draft.eraseStrokes = [];
    });
  }

  // ── Material layers (visual show/hide — never affects exported data) ──
  function toggleLayerHidden(layerKey) {
    setHiddenLayerKeys((prev) => {
      const next = new Set(prev);
      if (next.has(layerKey)) next.delete(layerKey);
      else next.add(layerKey);
      return next;
    });
  }

  // ── Zoom ───────────────────────────────────────────────
  const handleZoomIn = useCallback(() => {
    setZoom((prev) =>
      Math.min(ZOOM_MAX, Number((prev + ZOOM_STEP).toFixed(2))),
    );
  }, []);

  const handleZoomOut = useCallback(() => {
    setZoom((prev) =>
      Math.max(ZOOM_MIN, Number((prev - ZOOM_STEP).toFixed(2))),
    );
  }, []);

  // ── Mask rendering: Worker path + main-thread fallback ─
  // Lazily creates (and memoizes in a ref) the mask-render Worker. Returns
  // null — never throws — if OffscreenCanvas/Worker isn't available or
  // construction fails, so callers can fall back to the main thread.
  const getMaskWorker = useCallback(() => {
    if (typeof OffscreenCanvas === "undefined" || typeof Worker === "undefined") {
      return null;
    }
    if (maskWorkerRef.current) return maskWorkerRef.current;
    try {
      const worker = new Worker(
        new URL("../workers/maskRenderWorker.js", import.meta.url),
        { type: "module" },
      );
      worker.onmessage = (event) => {
        const { id, material_masks, error } = event.data;
        const pending = maskWorkerRequestsRef.current.get(id);
        if (!pending) return;
        maskWorkerRequestsRef.current.delete(id);
        if (error) pending.reject(new Error(error));
        else pending.resolve(material_masks);
      };
      worker.onerror = (event) => {
        for (const pending of maskWorkerRequestsRef.current.values()) {
          pending.reject(event.error || new Error("Mask render worker failed"));
        }
        maskWorkerRequestsRef.current.clear();
      };
      maskWorkerRef.current = worker;
      return worker;
    } catch {
      return null;
    }
  }, []);

  const getTransferableLayerBitmap = useCallback(
    async (layer) => {
      const img = layerImages[layer.key];
      if (!img) return null;
      let master = masterBitmapsRef.current.get(img);
      if (!master) {
        master = await createImageBitmap(img);
        masterBitmapsRef.current.set(img, master);
      }
      // A fresh clone per call — transfer below consumes it, the master
      // stays intact for the next getMaskPayload() call.
      return createImageBitmap(master);
    },
    [layerImages],
  );

  // Returns null (never throws) on any failure so getMaskPayload() can fall
  // back to the main thread for this call.
  const renderMaskLayersViaWorker = useCallback(
    async (layerList, params) => {
      const worker = getMaskWorker();
      if (!worker) return null;
      const bitmapLayers = await Promise.all(
        layerList.map(async (layer) => ({
          key: layer.key,
          bitmap: await getTransferableLayerBitmap(layer),
        })),
      );
      const transferables = bitmapLayers.map((l) => l.bitmap).filter(Boolean);
      const id = ++maskWorkerIdRef.current;
      return new Promise((resolve, reject) => {
        maskWorkerRequestsRef.current.set(id, { resolve, reject });
        try {
          worker.postMessage({ id, layers: bitmapLayers, ...params }, transferables);
        } catch (err) {
          maskWorkerRequestsRef.current.delete(id);
          reject(err);
        }
      });
    },
    [getMaskWorker, getTransferableLayerBitmap],
  );

  const renderMaskLayersOnMainThread = useCallback(
    (layerList, params) =>
      Promise.all(
        layerList.map(async (layer) => {
          const canvas = document.createElement("canvas");
          const flattened = renderMaskLayer(canvas, {
            ...params,
            layerImage: layerImages[layer.key],
            layerKey: layer.key,
          });
          if (!flattened) return null;
          return {
            layer_key: layer.key,
            mask_b64: await canvasToBase64(flattened),
          };
        }),
      ),
    [layerImages],
  );

  // ── Imperative handle for parent to call getMaskPayload() ──
  // One black/white PNG per material layer (black background, white = wall):
  // this layer's detected content, shared erase strokes punched out, this
  // layer's own vector walls painted white, OTHER layers' walls punched out
  // (exclusivity — a user-drawn wall claims its pixels for one material only).
  // added_walls carries each drawn wall's EXACT thickness_mm (raster pixels
  // are too coarse — see masonry_detector's mm_per_px docstring).
  useImperativeHandle(
    ref,
    () => ({
      async getMaskPayload() {
        // Export at the mask's native resolution (not the upscaled paint-space
        // used for on-screen precision) so an unedited mask round-trips
        // byte-identical instead of drifting through a double nearest-neighbor
        // resample (upscale here, then the backend's downscale back to its grid).
        const exportWidth = nativeWidth;
        const exportHeight = nativeHeight;
        const scaleX = paintWidth > 0 ? nativeWidth / paintWidth : 1;
        const scaleY = paintHeight > 0 ? nativeHeight / paintHeight : 1;
        const lineScale = (scaleX + scaleY) / 2;
        const wallElements = elements.filter((el) => el.type === "wall");

        const renderParams = {
          eraseStrokes,
          wallElements,
          activeMaterialKey,
          scaleX,
          scaleY,
          lineScale,
          exportWidth,
          exportHeight,
        };

        // Rendering itself (drawing + PNG encode per material layer) runs in
        // a Worker off the main thread; falls back to doing the identical
        // work here on the main thread if the Worker/OffscreenCanvas isn't
        // available or this specific call fails for any reason.
        let material_masks = await renderMaskLayersViaWorker(
          layers,
          renderParams,
        ).catch(() => null);
        if (!material_masks) {
          material_masks = await renderMaskLayersOnMainThread(
            layers,
            renderParams,
          );
        }
        material_masks = material_masks.filter((m) => m && m.mask_b64);

        const added_walls = wallElements
          .filter((wall) => Number.isFinite(wall.thicknessMm) && wall.thicknessMm > 0)
          .map((wall) => ({
            layer_key: wall.materialKey || activeMaterialKey,
            thickness_mm: wall.thicknessMm,
            points: [
              [wall.points[0].x * scaleX, wall.points[0].y * scaleY],
              [wall.points[1].x * scaleX, wall.points[1].y * scaleY],
            ],
          }));

        // Corrections for EXISTING (already-detected) walls, referenced by
        // position (bbox_pct, page-percent — unchanged since the wall was
        // last shown), not by id: wall ids are re-assigned from scratch on
        // every preview/recompute call.
        const thickness_overrides_out = thicknessOverrides.map((o) => ({
          layer_key: o.layerKey,
          bbox_pct: o.bboxPct,
          thickness_mm: o.thicknessMm,
        }));
        const material_overrides_out = materialOverrides.map((o) => ({
          layer_key: o.layerKey,
          bbox_pct: o.bboxPct,
        }));
        const wall_type_overrides_out = wallTypeOverrides.map((o) => ({
          bbox_pct: o.bboxPct,
          wall_type: o.wallType,
        }));

        return {
          material_masks,
          added_walls,
          thickness_overrides: thickness_overrides_out,
          material_overrides: material_overrides_out,
          wall_type_overrides: wall_type_overrides_out,
        };
      },
      hasUnsavedEdits() {
        return dirtyRef.current;
      },
      markSaved() {
        dirtyRef.current = false;
      },
    }),
    [
      layers,
      elements,
      eraseStrokes,
      activeMaterialKey,
      thicknessOverrides,
      materialOverrides,
      wallTypeOverrides,
      nativeWidth,
      nativeHeight,
      paintHeight,
      paintWidth,
      renderMaskLayersViaWorker,
      renderMaskLayersOnMainThread,
    ],
  );

  const isBusy = isPreviewing || isRecomputing;
  const normalizedErrorMessage = (errorMessage || "").toLowerCase();
  const isPreviewImageMissingError =
    normalizedErrorMessage.includes("preview") &&
    normalizedErrorMessage.includes("image") &&
    (normalizedErrorMessage.includes("not available") ||
      normalizedErrorMessage.includes("not found") ||
      normalizedErrorMessage.includes("missing"));
  const visibleErrorMessage = isPreviewImageMissingError ? "" : errorMessage;
  const eraserRefSize = designScale > 0 ? eraserSize / designScale : eraserSize;
  const brushSampleSize = Math.max(10, Math.min(44, eraserRefSize / 2));
  const hint =
    tool === "select"
      ? "Select a wall, room, or opening to edit details"
      : tool === "wall"
        ? "Click a wall start and end point"
        : tool === "room"
          ? "Click room corners; click near the first point to close"
          : tool === "erase"
            ? "Click objects to delete them, or drag over the plan to erase the detected mask"
            : "Click the plan to place an opening";

  const clampNumber = (value, min, max, fallback) =>
    Math.max(min, Math.min(max, Number(value) || fallback));

  return (
    <main
      className="tes-shell"
      ref={shellRef}
      role="dialog"
      aria-modal="true"
      aria-label={`Edit floor ${floorLabel || "Floor"}`}
      tabIndex={-1}
    >
      <header className="tes-topBar">
        <div className="tes-topBarLeft">
          <div className="tes-brandMark">
            <PencilRuler size={20} />
          </div>
          <div>
            <h1 className="ds-page-title tes-title">Edit floor {floorLabel || "Floor"}</h1>
            <p className="ds-page-subtitle">
              Refine the wall detection mask. Preview is a fast, no-AI
              estimate; Recompute re-runs full AI-based detection to apply
              changes (slower).
            </p>
          </div>
        </div>
        <div className="tes-headerActions">
          <button
            type="button"
            className="ds-btn ds-btn--secondary tes-actionButton"
            onClick={onPreview}
            disabled={isBusy}
            title="Fast, geometry-only estimate — no AI/vision model call."
          >
            {isPreviewing ? (
              <LoaderCircle size={16} className="ds-spin" aria-hidden="true" />
            ) : (
              <Eye size={16} />
            )}
            {isPreviewing ? "Previewing…" : "Preview floor"}
          </button>
          <button
            type="button"
            className="ds-btn ds-btn--primary tes-actionButton"
            onClick={onRecompute}
            disabled={isBusy}
            title="Full AI-based (VLM) wall/material re-detection — slower, may take a while."
          >
            {isRecomputing ? (
              <LoaderCircle size={16} className="ds-spin" aria-hidden="true" />
            ) : (
              <RotateCcw size={16} />
            )}
            {isRecomputing ? "Recomputing…" : "Recompute floor"}
          </button>
          <button
            type="button"
            className="ds-btn ds-btn--ghost tes-actionButton"
            onClick={onClose}
          >
            <X size={16} /> Close
          </button>
        </div>
      </header>

      <div className="tes-mainGrid">
        <section className="tes-boardPanel">
          <div className="tes-canvasToolbar">
            <div className="tes-toolGroup">
              <ToolbarButton
                label="Select"
                title="Select"
                active={tool === "select"}
                onClick={() => setTool("select")}
              >
                <MousePointer2 size={17} />
              </ToolbarButton>
              <ToolbarButton
                label="Wall"
                title="Draw wall"
                active={tool === "wall"}
                onClick={() => setTool("wall")}
              >
                <SquareDashed size={17} />
              </ToolbarButton>
              <ToolbarButton
                label="Room"
                title="Draw room"
                active={tool === "room"}
                onClick={() => setTool("room")}
              >
                <Square size={17} />
              </ToolbarButton>
              <ToolbarButton
                label="Opening"
                title="Place opening"
                active={tool === "opening"}
                onClick={() => setTool("opening")}
              >
                <DoorOpen size={17} />
              </ToolbarButton>
              <ToolbarButton
                label="Erase"
                title="Erase object"
                className="erase"
                active={tool === "erase"}
                onClick={() => setTool("erase")}
              >
                <Eraser size={17} />
              </ToolbarButton>
            </div>
            <div className="tes-toolGroup">
              <ToolbarButton
                label="Undo"
                title="Undo"
                onClick={undo}
                disabled={!history.length}
              >
                <Undo2 size={17} />
              </ToolbarButton>
              <ToolbarButton
                label="Redo"
                title="Redo"
                onClick={redo}
                disabled={!future.length}
              >
                <Redo2 size={17} />
              </ToolbarButton>
              {historyTruncated && (
                <span
                  className="tes-historyLimitNote"
                  title={`Only the last ${HISTORY_LIMIT} steps are kept — earlier history for this floor has been discarded.`}
                >
                  Undo limit reached
                </span>
              )}
              <ToolbarButton
                label="Grid"
                title="Toggle grid"
                active={showGrid}
                onClick={() => setShowGrid((value) => !value)}
              >
                <Grid3X3 size={17} />
              </ToolbarButton>
            </div>
            <div className="tes-zoomControls">
              <button type="button" onClick={handleZoomOut} title="Zoom out">
                <ZoomOut size={16} />
              </button>
              <span>{Math.round(zoom * 100)}%</span>
              <button type="button" onClick={handleZoomIn} title="Zoom in">
                <ZoomIn size={16} />
              </button>
            </div>
          </div>

          {visibleErrorMessage && (
            <div className="ds-alert ds-alert--danger tes-errorBanner" role="alert">
              <AlertCircle size={16} />
              {visibleErrorMessage}
            </div>
          )}

          <div className="tes-canvasFrame" ref={canvasWrapRef}>
            {layerImagesStatus === "loading" && !backgroundImage && (
              <div className="tes-frameStatus" role="status">
                <LoaderCircle size={14} className="ds-spin" aria-hidden="true" />
                Loading image…
              </div>
            )}
            {layerImagesStatus === "failed" && !backgroundImage && (
              <div className="tes-frameStatus error" role="alert">
                <AlertCircle size={16} />
                Failed to load image.
              </div>
            )}
            {showStage && (
              <svg
                ref={svgRef}
                className={`tes-blueprintCanvas ${tool === "erase" ? "eraseMode" : ""} ${
                  tool !== "select" && tool !== "erase" ? "drawMode" : ""
                }`}
                viewBox={`0 0 ${paintWidth} ${paintHeight}`}
                width={stageWidth}
                height={stageHeight}
                onClick={handleCanvasClick}
                onPointerDown={handleCanvasPointerDown}
                onPointerMove={handlePointerMove}
                onPointerUp={handlePointerUp}
                onPointerLeave={handleCanvasLeave}
              >
                <defs>
                  <pattern
                    id="tes-smallGrid"
                    width={gridSize}
                    height={gridSize}
                    patternUnits="userSpaceOnUse"
                  >
                    <path
                      d={`M ${gridSize} 0 L 0 0 0 ${gridSize}`}
                      fill="none"
                      stroke={CANVAS_COLORS.gridMinor}
                      strokeWidth={sz(DESIGN.gridMinorStroke)}
                    />
                  </pattern>
                  <pattern
                    id="tes-paperGrid"
                    width={gridSize * 5}
                    height={gridSize * 5}
                    patternUnits="userSpaceOnUse"
                  >
                    <rect
                      width={gridSize * 5}
                      height={gridSize * 5}
                      fill="url(#tes-smallGrid)"
                    />
                    <path
                      d={`M ${gridSize * 5} 0 L 0 0 0 ${gridSize * 5}`}
                      fill="none"
                      stroke={CANVAS_COLORS.gridMajor}
                      strokeWidth={sz(DESIGN.gridMajorStroke)}
                    />
                  </pattern>
                  {/* One luminance mask per material layer: that layer's
                      detected-wall PNG (white = wall) minus the shared erase
                      strokes. Applied to that material's own color fill so
                      each material renders in its own hue while black stays
                      fully transparent. */}
                  {layers.map((layer) => (
                    <mask
                      key={`mask-${layer.key}`}
                      id={`tes-wallMask-${layer.key}`}
                      maskUnits="userSpaceOnUse"
                      x="0"
                      y="0"
                      width={paintWidth}
                      height={paintHeight}
                    >
                      <rect width={paintWidth} height={paintHeight} fill="black" />
                      {layerImages[layer.key] && (
                        <image
                          href={layer.url}
                          x="0"
                          y="0"
                          width={paintWidth}
                          height={paintHeight}
                          preserveAspectRatio="none"
                        />
                      )}
                      {eraseStrokes.map((stroke, index) => (
                        <polyline
                          key={index}
                          points={flatToPointsAttr(stroke.points)}
                          fill="none"
                          stroke="black"
                          strokeWidth={stroke.width}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      ))}
                      {currentErase && currentErase.length >= 2 && (
                        <polyline
                          points={flatToPointsAttr(
                            ensureRenderableStroke(currentErase),
                          )}
                          fill="none"
                          stroke="black"
                          strokeWidth={eraserSize}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      )}
                    </mask>
                  ))}
                </defs>

                <rect width={paintWidth} height={paintHeight} fill={CANVAS_COLORS.paper} />
                {backgroundImage && (
                  <image
                    href={backgroundImageUrl}
                    x="0"
                    y="0"
                    width={paintWidth}
                    height={paintHeight}
                    opacity="0.95"
                    preserveAspectRatio="none"
                  />
                )}
                {layers.map(
                  (layer) =>
                    layerImages[layer.key] &&
                    !hiddenLayerKeys.has(layer.key) && (
                      <rect
                        key={`rect-${layer.key}`}
                        width={paintWidth}
                        height={paintHeight}
                        fill={layer.color || DETECTED_WALL_COLOR}
                        opacity="0.85"
                        mask={`url(#tes-wallMask-${layer.key})`}
                        pointerEvents="none"
                      />
                    ),
                )}
                {showGrid && (
                  <rect
                    width={paintWidth}
                    height={paintHeight}
                    fill="url(#tes-paperGrid)"
                    opacity="0.5"
                    pointerEvents="none"
                  />
                )}

                {/* Existing (auto-detected) walls. Hit-testing is a single
                    full-canvas rect resolving to a wall via resolveWallAt
                    (backend-exact hit_bbox_pct, not raster sampling) - the
                    per-wall rects below are outline-only. Sits below
                    rooms/walls/openings in z-order, so a user-drawn element
                    on top of a detected wall still takes click priority in
                    the overlap. */}
                <g className="tes-detectedWallLayer">
                  <rect
                    data-wall-hit-canvas="true"
                    x={0}
                    y={0}
                    width={paintWidth}
                    height={paintHeight}
                    fill="transparent"
                    pointerEvents={tool === "select" ? "fill" : "none"}
                    onMouseMove={handleDetectedWallLayerPointerMove}
                    onMouseLeave={handleDetectedWallLayerMouseLeave}
                    onClick={handleDetectedWallLayerClick}
                  />
                  {detectedWalls.map((wall) => (
                    <rect
                      key={wall.id}
                      data-detected-wall-id={wall.id}
                      x={Math.min(wall.x0, wall.x1)}
                      y={Math.min(wall.y0, wall.y1)}
                      width={Math.max(1, Math.abs(wall.x1 - wall.x0))}
                      height={Math.max(1, Math.abs(wall.y1 - wall.y0))}
                      fill="transparent"
                      stroke={
                        selectedGroupKey && groupKeyOf(wall) === selectedGroupKey
                          ? CANVAS_COLORS.selectionOutline
                          : hoveredGroupKey && groupKeyOf(wall) === hoveredGroupKey
                            ? wall.color || DETECTED_WALL_COLOR
                            : needsReview(wall)
                              ? CANVAS_COLORS.needsReview
                              : "transparent"
                      }
                      strokeWidth={sz(3)}
                      strokeDasharray={
                        selectedGroupKey && groupKeyOf(wall) === selectedGroupKey
                          ? `${sz(6)} ${sz(4)}`
                          : needsReview(wall) &&
                              !(selectedGroupKey && groupKeyOf(wall) === selectedGroupKey) &&
                              !(hoveredGroupKey && groupKeyOf(wall) === hoveredGroupKey)
                            ? `${sz(4)} ${sz(3)}`
                            : undefined
                      }
                      pointerEvents="none"
                      className="tes-detectedWallBox"
                    />
                  ))}
                </g>

                <g className="tes-roomLayer">
                  {rooms.map((room) => (
                    <g
                      key={room.id}
                      data-element-id={room.id}
                      onPointerDown={(event) =>
                        startElementDrag(event, room.id)
                      }
                    >
                      <polygon
                        points={room.polygon
                          .map((p) => `${p.x},${p.y}`)
                          .join(" ")}
                        fill={room.color}
                        stroke={selectedId === room.id ? CANVAS_COLORS.selectedShape : room.color}
                        strokeWidth={sz(selectedId === room.id ? 4 : 2)}
                        opacity="0.52"
                      />
                      <text
                        x={room.polygon[0].x + sz(DESIGN.roomLabelOffsetX)}
                        y={room.polygon[0].y + sz(DESIGN.roomLabelOffsetY)}
                        className="tes-roomLabel"
                        fontSize={sz(DESIGN.roomLabelFont)}
                      >
                        {room.name}
                      </text>
                      {selectedId === room.id &&
                        room.polygon.map((p, index) => (
                          <circle
                            key={index}
                            cx={p.x}
                            cy={p.y}
                            r={sz(DESIGN.handleRadius)}
                            strokeWidth={sz(DESIGN.handleStroke)}
                            className="tes-handle"
                            onPointerDown={(event) =>
                              startElementDrag(event, room.id, index)
                            }
                          />
                        ))}
                    </g>
                  ))}
                </g>

                <g className="tes-wallLayer">
                  {walls.map((wall) => (
                    <g
                      key={wall.id}
                      data-element-id={wall.id}
                      onPointerDown={(event) =>
                        startElementDrag(event, wall.id)
                      }
                    >
                      <line
                        x1={wall.points[0].x}
                        y1={wall.points[0].y}
                        x2={wall.points[1].x}
                        y2={wall.points[1].y}
                        stroke={wallStrokeColor(wall)}
                        strokeWidth={wall.thickness}
                        strokeLinecap="square"
                        opacity="0.88"
                      />
                      <line
                        x1={wall.points[0].x}
                        y1={wall.points[0].y}
                        x2={wall.points[1].x}
                        y2={wall.points[1].y}
                        stroke={
                          selectedId === wall.id ? CANVAS_COLORS.selectionOutline : "transparent"
                        }
                        strokeWidth={wall.thickness + sz(DESIGN.selectionExtra)}
                        strokeLinecap="square"
                        opacity="0.55"
                        pointerEvents="stroke"
                      />
                      {selectedId === wall.id &&
                        wall.points.map((p, index) => (
                          <circle
                            key={index}
                            cx={p.x}
                            cy={p.y}
                            r={sz(DESIGN.handleRadius)}
                            strokeWidth={sz(DESIGN.handleStroke)}
                            className="tes-handle"
                            onPointerDown={(event) =>
                              startElementDrag(event, wall.id, index)
                            }
                          />
                        ))}
                    </g>
                  ))}
                </g>

                <g className="tes-openingLayer">
                  {openings.map((opening) => (
                    <g
                      key={opening.id}
                      data-element-id={opening.id}
                      transform={`translate(${opening.x} ${opening.y}) rotate(${opening.rotation})`}
                      onPointerDown={(event) =>
                        startElementDrag(event, opening.id)
                      }
                    >
                      <rect
                        x={-opening.width / 2}
                        y={-sz(DESIGN.openingLeafHalf)}
                        width={opening.width}
                        height={sz(DESIGN.openingLeafHalf) * 2}
                        fill={CANVAS_COLORS.paper}
                        stroke={
                          selectedId === opening.id ? CANVAS_COLORS.selectedShape : CANVAS_COLORS.opening
                        }
                        strokeWidth={sz(4)}
                      />
                      <path
                        d={`M ${-opening.width / 2} ${sz(DESIGN.openingLeafHalf)} A ${opening.width} ${opening.width} 0 0 1 ${opening.width / 2} ${-sz(DESIGN.openingLeafHalf)}`}
                        fill="none"
                        stroke={CANVAS_COLORS.opening}
                        strokeWidth={sz(3)}
                        strokeDasharray={`${sz(8)} ${sz(8)}`}
                      />
                      {selectedId === opening.id && (
                        <>
                          <circle
                            cx={-opening.width / 2}
                            cy="0"
                            r={sz(DESIGN.handleRadius)}
                            strokeWidth={sz(DESIGN.handleStroke)}
                            className="tes-handle"
                            onPointerDown={(event) =>
                              startElementDrag(
                                event,
                                opening.id,
                                "opening-width",
                              )
                            }
                          />
                          <circle
                            cx={opening.width / 2}
                            cy="0"
                            r={sz(DESIGN.handleRadius)}
                            strokeWidth={sz(DESIGN.handleStroke)}
                            className="tes-handle"
                            onPointerDown={(event) =>
                              startElementDrag(
                                event,
                                opening.id,
                                "opening-width",
                              )
                            }
                          />
                        </>
                      )}
                    </g>
                  ))}
                </g>

                {draftPoints.length > 0 && (
                  <g className="tes-draftLayer">
                    {tool === "room" && (
                      <polyline
                        points={draftPoints
                          .map((p) => `${p.x},${p.y}`)
                          .join(" ")}
                        fill="none"
                        stroke={CANVAS_COLORS.draft}
                        strokeWidth={sz(DESIGN.draftStroke)}
                        strokeDasharray={`${sz(12)} ${sz(10)}`}
                      />
                    )}
                    {tool === "wall" && (
                      <circle
                        cx={draftPoints[0].x}
                        cy={draftPoints[0].y}
                        r={sz(DESIGN.draftStartRadius)}
                        fill={CANVAS_COLORS.draft}
                      />
                    )}
                    {draftPoints.map((p, index) => (
                      <circle
                        key={index}
                        cx={p.x}
                        cy={p.y}
                        r={sz(DESIGN.draftPointRadius)}
                        fill={CANVAS_COLORS.draft}
                      />
                    ))}
                  </g>
                )}

                {tool === "erase" && brushPreview && (
                  <g className="tes-brushPreview" pointerEvents="none">
                    <circle
                      cx={brushPreview.x}
                      cy={brushPreview.y}
                      r={eraserSize / 2}
                      strokeWidth={sz(3)}
                      strokeDasharray={`${sz(10)} ${sz(8)}`}
                    />
                    <line
                      x1={brushPreview.x - eraserSize / 2}
                      y1={brushPreview.y}
                      x2={brushPreview.x + eraserSize / 2}
                      y2={brushPreview.y}
                      strokeWidth={sz(2)}
                    />
                    <line
                      x1={brushPreview.x}
                      y1={brushPreview.y - eraserSize / 2}
                      x2={brushPreview.x}
                      y2={brushPreview.y + eraserSize / 2}
                      strokeWidth={sz(2)}
                    />
                  </g>
                )}
              </svg>
            )}
          </div>

          {tool === "erase" && (
            <div className="tes-eraseBanner">
              <Eraser size={17} />
              <strong>Erase mode</strong>
              <span>
                Click objects to remove them, or drag on the plan to erase the
                detected mask.
              </span>
            </div>
          )}

          <footer className="tes-bottomBar">
            <span>{hint}</span>
          </footer>
        </section>

        <aside className="tes-inspector">
          {(overallConfidence || validation || (lvComparison && lvComparison.length > 0) || flaggedWallCount > 0 || undisplayableWallCount > 0) && (
            <section className="tes-panel">
              <div className="tes-panelTitle">
                <div>
                  <strong>Data quality</strong>
                  <span>
                    {hasDataQualityIssue
                      ? "Issues flagged for this extraction"
                      : "AI confidence for this extraction"}
                  </span>
                </div>
                {overallConfidence && (
                  <span
                    className={confidenceBadgeClass(overallConfidence)}
                    title="Overall confidence the AI reports for this extraction — LOW means double-check the numbers before relying on them."
                  >
                    {overallConfidence}
                  </span>
                )}
              </div>
              {overallConfidence && !hasDataQualityIssue && (
                <div className="ds-alert ds-alert--success tes-note">
                  <Check size={17} />
                  <div>
                    <strong>Nothing flagged</strong>
                    <span>All walls resolved with recognized materials.</span>
                  </div>
                </div>
              )}
              {validation?.escalated && (
                <div className="ds-alert ds-alert--danger tes-note">
                  <AlertCircle size={18} />
                  <div>
                    <strong>This result needs human review</strong>
                    {validation.escalation_reasons?.length ? (
                      <ul className="tes-reasonList">
                        {validation.escalation_reasons.map((reason, i) => (
                          <ReasonRow
                            key={i}
                            reason={reason}
                            currentPageIndex={pageIndex}
                            onJumpToReason={onJumpToReason}
                          />
                        ))}
                      </ul>
                    ) : (
                      <span>
                        An aggregate consistency check flagged this extraction as unreliable —
                        verify key measurements before relying on the totals.
                      </span>
                    )}
                  </div>
                </div>
              )}
              {validation?.flags?.length > 0 && (
                <ul className="tes-flagsList">
                  {validation.flags.map((flag, i) => (
                    <ReasonRow
                      key={i}
                      reason={flag}
                      currentPageIndex={pageIndex}
                      onJumpToReason={onJumpToReason}
                    />
                  ))}
                </ul>
              )}
              {lvComparison?.filter((g) => g.status !== "ok").length > 0 && (
                <div className="tes-lvTable">
                  {lvComparison
                    .filter((g) => g.status !== "ok")
                    .map((g) => (
                      <div key={g.group} className={`tes-lvRow tes-lv-${g.status}`}>
                        <div className="tes-lvRowTitle">
                          <span>{g.group}</span>
                          <span>
                            {g.extracted_net_m2?.toFixed?.(1) ?? "0.0"} /{" "}
                            {g.lv_m2?.toFixed?.(1) ?? "—"} m²
                          </span>
                        </div>
                        {g.note && <div className="tes-lvRowNote">{g.note}</div>}
                      </div>
                    ))}
                </div>
              )}
              {flaggedWallCount > 0 && (
                <div className="tes-measureReviewFlag">
                  <div>
                    ⚠ {flaggedWallCount} wall{flaggedWallCount === 1 ? "" : "s"} flagged for
                    manual review — dashed amber outline on the plan.
                  </div>
                  <button
                    type="button"
                    className="tes-compactButton tes-nextUnresolvedBtn"
                    onClick={handleSelectNextUnresolved}
                    title="Select the next flagged wall so its Material/Wall type can be corrected"
                  >
                    Next unresolved wall →{" "}
                    {flaggedWallCursor >= 0
                      ? `(${flaggedWallCursor + 1} of ${flaggedWallCount})`
                      : `(${flaggedWallCount})`}
                  </button>
                </div>
              )}
              {undisplayableWallCount > 0 && (
                <div className="tes-measureReviewFlag">
                  ⚠ {undisplayableWallCount} wall{undisplayableWallCount === 1 ? "" : "s"} on
                  this floor could not be displayed (missing position data) — a validation
                  message may refer to one of these; try re-running extraction for this
                  floor.
                </div>
              )}
            </section>
          )}

          {jobId && (
            <section className="tes-panel">
              <button type="button" className="tes-historyToggle" onClick={toggleHistory}>
                <History size={16} />
                <span>Change history</span>
                <span className="tes-historyToggleHint">{historyOpen ? "Hide" : "Show"}</span>
              </button>
              {historyOpen && (
                <div className="tes-historyList">
                  {historyLoading && (
                    <div className="tes-historyEmpty" role="status">
                      <LoaderCircle size={12} className="ds-spin" aria-hidden="true" /> Loading…
                    </div>
                  )}
                  {historyError && (
                    <div className="ds-alert ds-alert--danger tes-note" role="alert">
                      {historyError}
                    </div>
                  )}
                  {!historyLoading && !historyError && historyEntries?.length === 0 && (
                    <div className="tes-historyEmpty">No corrections recorded yet.</div>
                  )}
                  {!historyLoading && !historyError && historyEntries?.map((entry) => (
                    <div key={entry.id} className="tes-historyRow">
                      <span
                        className={`ds-badge ${
                          entry.source === "agent_auto_fix" ? "ds-badge--info" : "ds-badge--success"
                        }`}
                      >
                        {entry.source === "agent_auto_fix" ? "Agent" : "You"}
                      </span>
                      <div className="tes-historyDetail">
                        <span>{formatHistoryChange(entry)}</span>
                        {entry.reasoning && (
                          <span className="tes-historyReasoning">{entry.reasoning}</span>
                        )}
                      </div>
                      <span className="tes-historyTime">
                        {entry.created_at ? new Date(entry.created_at).toLocaleString() : ""}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </section>
          )}

          <section className="tes-panel">
            <div className="tes-panelTitle">
              <div>
                <strong>Layer controls</strong>
                <span>
                  Tools and selected object
                  {pageIndex ? ` · Page ${pageIndex}` : ""}
                </span>
              </div>
              <button
                type="button"
                className="tes-miniButton"
                onClick={clearLayers}
                title="Clear drawn layers"
                disabled={!elements.length}
              >
                <RotateCcw size={15} />
              </button>
            </div>

            <div className="ds-alert ds-alert--success tes-note">
              <Check size={17} />
              <div>
                <strong>Mask editing active</strong>
                <span>
                  Walls are baked white into the mask on preview/recompute.
                  Erase strokes remove detected areas. Rooms and openings are
                  visual annotations only.
                </span>
              </div>
            </div>

            {layers.length > 0 && (
              <div className="tes-materialList">
                {layers.map((layer) => (
                  <button
                    type="button"
                    key={layer.key}
                    className={`tes-materialChip ${chipHighlightKey === layer.key ? "active" : ""}`}
                    onClick={() => setActiveMaterialKey(layer.key)}
                    title={`Paint ${layer.label} walls`}
                  >
                    <HatchSwatch
                      color={layer.color}
                      hatchFeatures={layer.hatchFeatures}
                      imageB64={layer.swatchImageB64}
                      size={16}
                    />
                    <span className="tes-materialLabel">{layer.label}</span>
                    <span
                      role="button"
                      tabIndex={0}
                      className="tes-materialEye"
                      title={hiddenLayerKeys.has(layer.key) ? "Show layer" : "Hide layer"}
                      onClick={(event) => {
                        event.stopPropagation();
                        toggleLayerHidden(layer.key);
                      }}
                      onKeyDown={(event) => {
                        if (event.key !== "Enter" && event.key !== " ") return;
                        event.preventDefault();
                        event.stopPropagation();
                        toggleLayerHidden(layer.key);
                      }}
                    >
                      <Eye
                        size={14}
                        style={{ opacity: hiddenLayerKeys.has(layer.key) ? 0.3 : 1 }}
                      />
                    </span>
                  </button>
                ))}
              </div>
            )}

            <div className="tes-quickSettings">
              <label>
                Eraser brush
                <div className="tes-brushControl">
                  <div
                    className="tes-brushSample"
                    style={{ "--brush-size": `${brushSampleSize}px` }}
                  >
                    <span />
                  </div>
                  <div className="tes-brushInputs">
                    <input
                      type="range"
                      min={eraserMin}
                      max={eraserMax}
                      value={eraserSize}
                      onChange={(event) =>
                        setEraserSizeOverride(
                          clampNumber(
                            event.target.value,
                            eraserMin,
                            eraserMax,
                            eraserSize,
                          ),
                        )
                      }
                      aria-label="Eraser brush size"
                    />
                    <div className="tes-unitInput">
                      <input
                        type="number"
                        min={eraserMin}
                        max={eraserMax}
                        value={eraserSize}
                        onChange={(event) =>
                          setEraserSizeOverride(
                            clampNumber(
                              event.target.value,
                              eraserMin,
                              eraserMax,
                              eraserSize,
                            ),
                          )
                        }
                        aria-label="Eraser brush size in pixels"
                      />
                      <span>px</span>
                    </div>
                  </div>
                </div>
              </label>
              {Boolean(eraseStrokes.length) && (
                <button
                  type="button"
                  className="ds-btn ds-btn--secondary tes-actionButton"
                  onClick={clearEraseStrokes}
                >
                  <Eraser size={15} /> Clear mask erases
                </button>
              )}
              <label>
                Grid size
                <input
                  type="number"
                  min={gridMin}
                  max={gridMax}
                  value={gridSize}
                  onChange={(event) =>
                    setGridSizeOverride(
                      clampNumber(
                        event.target.value,
                        gridMin,
                        gridMax,
                        gridSize,
                      ),
                    )
                  }
                />
              </label>
              {tool === "wall" && (
                <label>
                  Wall thickness
                  <div className="tes-toolGroup">
                    {presetsMm.map((mm) => (
                      <ToolbarButton
                        key={mm}
                        label={`${(mm / 10).toFixed(1)} cm`}
                        title={`${(mm / 10).toFixed(1)} cm wall`}
                        active={selectedThicknessMm === mm}
                        onClick={() => setSelectedThicknessMm(mm)}
                      />
                    ))}
                  </div>
                </label>
              )}
            </div>

            {selected ? (
              <div className="tes-properties">
                <label>
                  Name
                  <input
                    value={selected.name || selected.wallType || selected.type}
                    onChange={(event) =>
                      updateSelected({ name: event.target.value })
                    }
                  />
                </label>
                {selected.type === "wall" && (
                  <>
                    <label>
                      Wall type
                      <select
                        value={selected.wallType}
                        onChange={(event) =>
                          updateSelected({ wallType: event.target.value })
                        }
                      >
                        <option value="inner">Inner wall</option>
                        <option value="outer">Outer wall</option>
                      </select>
                    </label>
                    {layers.length > 0 && (
                      <label>
                        Material
                        <select
                          value={selected.materialKey || activeMaterialKey}
                          onChange={(event) =>
                            updateSelected(
                              { materialKey: event.target.value },
                              true,
                            )
                          }
                        >
                          {layers.map((layer) => (
                            <option key={layer.key} value={layer.key}>
                              {layer.label}
                            </option>
                          ))}
                        </select>
                      </label>
                    )}
                    <label>
                      Thickness
                      <div className="tes-unitInput">
                        <input
                          type="number"
                          min="1"
                          step="0.5"
                          value={
                            Number.isFinite(selected.thicknessMm)
                              ? (selected.thicknessMm / 10).toFixed(1)
                              : ""
                          }
                          onChange={(event) => {
                            const cm = Number(event.target.value);
                            if (!Number.isFinite(cm) || cm <= 0) return;
                            const mm = cm * 10;
                            updateSelected(
                              { thickness: mmToPaintPx(mm), thicknessMm: mm },
                              true,
                            );
                          }}
                        />
                        <span>cm</span>
                      </div>
                      <input
                        type="range"
                        min={thicknessMin}
                        max={thicknessMax}
                        value={selected.thickness}
                        onChange={(event) => {
                          const px = Number(event.target.value);
                          updateSelected(
                            {
                              thickness: px,
                              thicknessMm: paintPxToMm(px) ?? selected.thicknessMm,
                            },
                            true,
                          );
                        }}
                      />
                    </label>
                    <label>
                      Wall length
                      <div className="tes-unitInput">
                        <input
                          type="number"
                          min="1"
                          value={Math.round(wallLength(selected))}
                          onChange={(event) => {
                            const length = Math.max(
                              1,
                              Number(event.target.value) || 1,
                            );
                            commit((draft) => {
                              const item = draft.elements.find(
                                (element) => element.id === selectedId,
                              );
                              if (item) setWallLengthFromStart(item, length);
                            });
                            markMaskEdited();
                          }}
                        />
                        <span>px</span>
                      </div>
                    </label>
                  </>
                )}
                {selected.type === "room" && (
                  <label>
                    Color
                    <div className="tes-swatches">
                      {ROOM_COLORS.map((color) => (
                        <button
                          type="button"
                          key={color}
                          style={{ background: color }}
                          className={selected.color === color ? "picked" : ""}
                          onClick={() => updateSelected({ color })}
                        />
                      ))}
                    </div>
                  </label>
                )}
                {selected.type === "opening" && (
                  <>
                    <label>
                      Width
                      <div className="tes-unitInput">
                        <input
                          type="number"
                          min={openingMin}
                          max={openingMax}
                          value={Math.round(selected.width)}
                          onChange={(event) =>
                            updateSelected({
                              width: clampNumber(
                                event.target.value,
                                openingMin,
                                openingMax,
                                selected.width,
                              ),
                            })
                          }
                        />
                        <span>px</span>
                      </div>
                      <input
                        type="range"
                        min={openingMin}
                        max={openingMax}
                        value={selected.width}
                        onChange={(event) =>
                          updateSelected({
                            width: Number(event.target.value),
                          })
                        }
                      />
                    </label>
                    <label>
                      Rotation
                      <input
                        type="range"
                        min="0"
                        max="180"
                        value={selected.rotation}
                        onChange={(event) =>
                          updateSelected({
                            rotation: Number(event.target.value),
                          })
                        }
                      />
                    </label>
                  </>
                )}
                <div className="tes-propertyActions">
                  <button
                    type="button"
                    className="ds-btn ds-btn--secondary tes-actionButton"
                    onClick={duplicateSelected}
                  >
                    <Copy size={15} /> Duplicate
                  </button>
                  <button
                    type="button"
                    className="ds-btn ds-btn--danger tes-actionButton"
                    onClick={deleteSelected}
                  >
                    <Trash2 size={15} /> Delete
                  </button>
                </div>
              </div>
            ) : displayedDetectedWall ? (
              <div className="tes-properties">
                {displayedDetectedWall.needs_review && (
                  <div className="ds-alert ds-alert--warning tes-note">
                    ⚠ {displayedDetectedWall.review_reason || "Needs your input."}
                  </div>
                )}
                <label>
                  Material
                  {layers.length > 0 ? (
                    <select
                      value={
                        pendingMaterialOverride?.layerKey ??
                        displayedDetectedWall.layer_key ??
                        ""
                      }
                      disabled={displayedDetectedWall.id !== selectedDetectedWall?.id}
                      onChange={(event) =>
                        upsertMaterialOverride(selectedDetectedWall.bbox_pct, event.target.value)
                      }
                    >
                      {layers.map((layer) => (
                        <option key={layer.key} value={layer.key}>
                          {layer.label}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      value={
                        displayedDetectedWall.label ||
                        displayedDetectedWall.material ||
                        "—"
                      }
                      disabled
                    />
                  )}
                </label>
                <label>
                  Wall type
                  <select
                    value={
                      pendingWallTypeOverride?.wallType ??
                      (displayedDetectedWall.wall_type === "aussen" ||
                      displayedDetectedWall.wall_type === "innen"
                        ? displayedDetectedWall.wall_type
                        : "")
                    }
                    disabled={displayedDetectedWall.id !== selectedDetectedWall?.id}
                    onChange={(event) =>
                      upsertWallTypeOverride(selectedDetectedWall.bbox_pct, event.target.value)
                    }
                  >
                    <option value="" disabled>
                      {displayedDetectedWall.wall_type === "unbekannt" || !displayedDetectedWall.wall_type
                        ? "Unbekannt — pick one"
                        : "—"}
                    </option>
                    <option value="aussen">Outer wall (außen)</option>
                    <option value="innen">Inner wall (innen)</option>
                  </select>
                </label>
                <label>
                  Length
                  <div className="tes-unitInput">
                    <input
                      value={
                        Number.isFinite(displayedDetectedWall.full_length_m)
                          ? displayedDetectedWall.full_length_m.toFixed(2)
                          : "—"
                      }
                      disabled
                    />
                    <span>m</span>
                  </div>
                </label>
                <label>
                  Thickness
                  <div className="tes-unitInput">
                    <input
                      type="number"
                      min="1"
                      step="0.5"
                      value={
                        Number.isFinite(displayedDetectedWall.thickness_mm)
                          ? (displayedDetectedWall.thickness_mm / 10).toFixed(1)
                          : ""
                      }
                      disabled={displayedDetectedWall.id !== selectedDetectedWall?.id}
                      onChange={(event) => {
                        const cm = Number(event.target.value);
                        if (!Number.isFinite(cm) || cm <= 0) return;
                        upsertThicknessOverride(
                          selectedDetectedWall.bbox_pct,
                          selectedDetectedWall.layer_key,
                          cm * 10,
                        );
                      }}
                    />
                    <span>cm</span>
                  </div>
                </label>
                <span className="tes-detectedWallHint">
                  {displayedDetectedWall.id === selectedDetectedWall?.id
                    ? "Detected wall — correct the material, wall type, or thickness if any were misread."
                    : "Hover a wall to preview its properties, click to select and correct it."}
                </span>
              </div>
            ) : null}
          </section>

          <section className="tes-panel">
            <div className="tes-panelTitle">
              <div>
                <strong>Measurements</strong>
                <span>
                  {measurementsStatus === "estimate"
                    ? "Live estimate from your edits"
                    : measurementsStatus === "loading"
                      ? "Estimating…"
                      : "Saved (last recompute)"}
                </span>
              </div>
              {measurementsStatus === "loading" && (
                <LoaderCircle size={14} className="ds-spin" aria-hidden="true" />
              )}
            </div>
            {displayedDetectedWall && (
              <div className="tes-measureHover">
                <span
                  className="tes-materialDot"
                  style={{ background: displayedDetectedWall.color || DETECTED_WALL_COLOR }}
                />
                <div className="tes-measureRowBody">
                  <span className="tes-measureLabel">
                    {displayedDetectedWall.label || displayedDetectedWall.material || "—"}
                  </span>
                  <span className="tes-measureStats">
                    {Number.isFinite(displayedDetectedWall.full_length_m)
                      ? `${displayedDetectedWall.full_length_m.toFixed(2)} m`
                      : "— m"}
                    {Number.isFinite(displayedDetectedWall.thickness_mm)
                      ? ` · d=${(displayedDetectedWall.thickness_mm / 10).toFixed(1)} cm`
                      : ""}
                    {(() => {
                      const area =
                        displayedDetectedWall.net_area_m2 ??
                        displayedDetectedWall.gross_area_m2 ??
                        (Number.isFinite(displayedDetectedWall.full_length_m) &&
                        Number.isFinite(measurements?.floor_height_m)
                          ? displayedDetectedWall.full_length_m * measurements.floor_height_m
                          : null);
                      return Number.isFinite(area) ? ` · ${area.toFixed(2)} m²` : "";
                    })()}
                  </span>
                  {needsReview(displayedDetectedWall) && (
                    <span className="tes-measureReviewFlag">
                      ⚠ {reviewReason(displayedDetectedWall)}
                    </span>
                  )}
                </div>
              </div>
            )}
            {measurements?.by_material?.length ? (
              <div className="tes-measureList">
                {measurements.by_material.map((m) => {
                  const gross = m.gross_m2 ?? m.total_gross_m2;
                  return (
                    <div key={m.layer_key} className="tes-measureRow">
                      <span
                        className="tes-materialDot"
                        style={{ background: m.color || DETECTED_WALL_COLOR }}
                      />
                      <div className="tes-measureRowBody">
                        <span className="tes-measureLabel">{m.label}</span>
                        <span className="tes-measureStats">
                          {m.wall_count} wall{m.wall_count === 1 ? "" : "s"} ·{" "}
                          {(m.total_length_m ?? 0).toFixed(2)} m
                          {typeof gross === "number"
                            ? ` · ${gross.toFixed(2)} m² gross`
                            : ""}
                          {typeof m.net_m2 === "number"
                            ? ` · ${m.net_m2.toFixed(2)} m² net`
                            : ""}
                        </span>
                        {m.by_thickness?.length > 1 && (
                          <span className="tes-measureByThickness">
                            {m.by_thickness
                              .map(
                                (t) =>
                                  `d=${(t.thickness_mm / 10).toFixed(1).replace(".", ",")}cm — ${t.gross_m2.toFixed(2)} m²`,
                              )
                              .join(" · ")}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
                <div className="tes-measureTotal">
                  Total: {measurements.wall_count ?? "–"} wall
                  {measurements.wall_count === 1 ? "" : "s"} ·{" "}
                  {(measurements.total_gross_m2 ?? 0).toFixed(2)} m²
                  {measurements.floor_height_m
                    ? ` @ ${measurements.floor_height_m.toFixed(2)} m height`
                    : ""}
                </div>
              </div>
            ) : (
              <div className="tes-measureEmpty">
                No measurements yet — Preview or Recompute to calculate.
              </div>
            )}
          </section>

          {previewImageUrl && (
            <section className="tes-panel">
              <div className="tes-panelTitle">
                <div>
                  <strong>Preview</strong>
                  <span>Latest floor preview</span>
                </div>
              </div>
              <img
                className="tes-previewImg"
                src={previewImageUrl}
                alt="Floor preview"
                onClick={onPreviewImageClick}
              />
            </section>
          )}
        </aside>
      </div>
    </main>
  );
});

export default TakeoffImageEditor;
